import React, { useState, useMemo, useEffect } from "react";
import { collection, limit, onSnapshot, orderBy, query, where, doc, updateDoc, or } from "firebase/firestore";
import { UserAuth } from "../context/AuthContext";
import { db } from "../firebase";
import { schema } from "../schema";
import moment from "moment";
import Loading from "./Loading";
import { getResultLabel, getResultColor, fmtDur, R_LABELS, R_COLORS, normalizeResult, getMillis, SITUATIONS_MAP } from "./helpers";
import NameManagerSection from "./NameManagerSection";
import ReviewInbox from "./ReviewInbox";
import SituationGuide from "./SituationGuide";
import CaseReport from "./CaseReport";
import CloseAlarmModal from "./CloseAlarmModal";
import { getReviewRoles, getReviewState, confirmEvent, validateEvent, isRelevantEvent, REVIEW_COLORS } from "../utils/review";
import EventDetail from "./EventDetail";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  LabelList,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

const TH = { color: "var(--muted)", fontWeight: 700, fontSize: 9, textTransform: "uppercase", letterSpacing: ".04em" };

// Valor comparable de un evento para ordenar el historial por columna
function historySortValue(a, key) {
  switch (key) {
    case "zoneName": return (a.zoneName || "").toLowerCase();
    case "date":     return a.createdAt?.seconds ? a.createdAt.seconds : 0;
    case "duration": return a.duration != null ? a.duration : -1;
    case "cctv":     return (a.dispatchedBy || a.guardName || "").toLowerCase();
    case "guard":    return ((Array.isArray(a.guards) ? a.guards[0] : "") || "").toLowerCase();
    case "result":   return a.resolvedAt ? (getResultLabel(a) || "").toLowerCase() : "zzzz"; // abiertos al final (asc)
    case "signal": {
      const v = a.signalBarsNum ?? (a.signalBars && a.signalBars !== "not_specified" ? Number(a.signalBars) : null);
      return v == null ? -1 : v;
    }
    case "plate":    return (a.vehicle?.plate || a.plate || "").toLowerCase();
    case "suspect":  return (a.suspectDesc || (a.suspect ? "si" : "") || "").toLowerCase();
    case "comment":  return (a.comments || a.notes || "").toLowerCase();
    case "review":   return { pending: 0, confirmed: 1, edited: 2, validated: 3 }[getReviewState(a)] ?? 4;
    default:         return 0;
  }
}

// Envuelve el nombre de una zona en varias líneas (evita nombres cortados en el eje Y).
function wrapZoneLabel(text, maxChars = 15, maxLines = 3) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = "";
  for (const w of words) {
    const candidate = cur ? cur + " " + w : w;
    if (candidate.length <= maxChars) {
      cur = candidate;
    } else {
      if (cur) lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = kept[maxLines - 1] + "…";
    return kept;
  }
  return lines.length ? lines : [""];
}

// Tick del eje Y para el ranking de zonas: nombre multilínea anclado a la derecha del eje.
function ZoneAxisTick({ x, y, payload }) {
  const lines = wrapZoneLabel(payload && payload.value);
  const lineHeight = 8;
  const startDy = -((lines.length - 1) * lineHeight) / 2;
  return (
    <text x={x} y={y} textAnchor="end" fill="var(--subtle)" fontSize={7.5} fontWeight={600}>
      {lines.map((ln, i) => (
        <tspan key={i} x={x} dy={i === 0 ? startDy : lineHeight}>{ln}</tspan>
      ))}
    </text>
  );
}

function KPI({ label, value, sub, color, compareText, compareColor, onClick }) {
  const isLongText = typeof value === "string" && value.length > 10 && !/^\d+(\.\d+)?(%)?$/.test(value) && value !== "N/A" && value !== "—";

  return (
    <div 
      onClick={onClick}
      className={onClick ? "interactive-kpi" : ""}
      style={{
        padding: "12px", background: "var(--card)",
        border: "1px solid var(--border)", borderRadius: 9, borderLeft: `4px solid ${color}`,
        minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "space-between",
        minHeight: 82, overflow: "hidden",
        cursor: onClick ? "pointer" : "default"
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", marginBottom: 4 }}>
        <span style={{ fontSize: 9, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</span>
        {compareText && (
          <span style={{
            fontSize: 8,
            fontWeight: 800,
            color: compareColor,
            background: `${compareColor}12`,
            padding: "1px 4px",
            borderRadius: 3,
            fontFamily: "var(--mono)",
            border: `1px solid ${compareColor}20`
          }}>
            {compareText}
          </span>
        )}
      </div>
      <div style={{
        fontSize: isLongText ? 20 : 22, 
        fontWeight: 800, 
        color, 
        fontFamily: isLongText ? "inherit" : "var(--mono)", 
        lineHeight: isLongText ? 1.25 : 1.35,
        overflow: "hidden", 
        textOverflow: "ellipsis", 
        display: isLongText ? "-webkit-box" : "block",
        WebkitLineClamp: isLongText ? 2 : "unset",
        WebkitBoxOrient: isLongText ? "vertical" : "unset",
        whiteSpace: isLongText ? "normal" : "nowrap",
        wordBreak: isLongText ? "break-word" : "normal",
        marginTop: 2, 
        marginBottom: 2
      }}>{value}</div>
      <div style={{
        fontSize: 9,
        color: "var(--muted)",
        marginTop: 4,
        lineHeight: 1.35,
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        wordBreak: "break-word"
      }}>{sub}</div>
    </div>
  );
}



function generateMockAlarms(zonesList) {
  const list = [];
  const operators = ["Carlos Ruiz", "Sofía Gómez", "Juan Pérez", "Marta Díaz"];
  const guardsList = ["Guardia 1", "Guardia 2", "Guardia 3", "Guardia 4"];
  const results = ["with_findings", "without_findings", "drill", "unmanaged"];
  const plates = ["BB-CC-11", "XX-YY-99", "AA-ZZ-55", "", ""];
  const suspectDescs = ["Sujeto con gorro merodeando", "Vehículo sospechoso detenido", "", ""];

  // Generate 25 alarms over the last 30 days
  for (let i = 0; i < 25; i++) {
    const zone = zonesList && zonesList.length > 0 
      ? zonesList[i % zonesList.length] 
      : { id: (i % 4) + 1, zoneName: `Zona ${(i % 4) + 1}` };

    const alarmTime = moment().subtract(i * 28, "hours"); // spread over 30 days
    const dispatchTime = moment(alarmTime).add(2, "minutes");
    const arrivedTime = moment(dispatchTime).add(5, "minutes");
    const resolvedTime = moment(arrivedTime).add(10, "minutes");
    const duration = resolvedTime.diff(alarmTime, "seconds");

    const closeResult = results[i % results.length];
    let situation = "3";
    if (closeResult === "with_findings") situation = "6";
    else if (closeResult === "drill") situation = "1";
    else if (closeResult === "unmanaged") situation = "2";

    const signalBarsNum = (i % 5) + 1; // 1 to 5

    list.push({
      docID: `mock-alarm-${i}`,
      zoneID: zone.id,
      zoneName: zone.zoneName,
      createdAt: { seconds: alarmTime.unix() },
      updatedAt: { seconds: resolvedTime.unix() },
      resolvedAt: { seconds: resolvedTime.unix() },
      duration: duration,
      closeResult: closeResult,
      reasonForResolution: closeResult,
      situation: situation,
      dispatchedBy: operators[i % operators.length],
      closedBy: closeResult === "unmanaged" ? "Sistema" : operators[(i + 1) % operators.length],
      guardName: guardsList[i % guardsList.length],
      guards: [guardsList[i % guardsList.length]],
      dispatchTime: { seconds: dispatchTime.unix() },
      arrivedTime: { seconds: arrivedTime.unix() },
      signalBars: String(signalBarsNum),
      signalBarsNum: signalBarsNum,
      plate: plates[i % plates.length],
      vehicle: { plate: plates[i % plates.length] },
      suspect: !!suspectDescs[i % suspectDescs.length],
      suspectDesc: suspectDescs[i % suspectDescs.length],
      notes: i % 2 === 0 ? "Se verificó área sin novedades mayores." : "Hallazgo de vehículo sospechoso reportado a supervisor.",
      reasonForResolutionDetails: i % 2 === 0 ? "Se verificó área sin novedades mayores." : "Hallazgo de vehículo sospechoso reportado a supervisor."
    });
  }

  return list;
}

/**
 * SupervisorView — KPI dashboard + guard/operator tables + event history.
 * Props:
 *   alarms: Firestore alarm docs (all alarms for this facility)
 */
export default function SupervisorView({ facility, zones, isSupervisor }) {
  const authContext = UserAuth ? UserAuth() : { user: null };
  const user = authContext?.user;
  // Usuario dev interno Tank (mismo acceso a Ajustes que el supervisor)
  const isDevUser = !!(user?.uid && facility?.devUID && user.uid === facility.devUID);
  // Roles de revisión/validación (Fase 2): supervisor/dev revisan; valida el
  // chiefUID si el recinto lo define, o el supervisor si no.
  const reviewRoles = getReviewRoles(facility, user);
  const [showInbox, setShowInbox] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [editAlarm, setEditAlarm] = useState(null); // alarma en corrección (CloseAlarmModal)
  const [reportCases, setReportCases] = useState(null); // array de casos para la ficha/reporte

  const [facilitiesList, setFacilitiesList] = useState(facility ? [facility] : []);
  const [selectedFacilityIds, setSelectedFacilityIds] = useState([]);
  const [facilityDropdownOpen, setFacilityDropdownOpen] = useState(false);
  const [allZones, setAllZones] = useState([]);

  const isCopec = useMemo(() => {
    const checkString = (str) => String(str || "").toLowerCase().includes("copec");
    return checkString(facility?.company) || checkString(facility?.title) ||
           facilitiesList.some(f => checkString(f.company) || checkString(f.title));
  }, [facility, facilitiesList]);

  const cleanCopecName = (name) => {
    if (!name) return "";
    let cleaned = name;
    // Replace "sucurdales copec", "sucursales copec", "sucursal copec" case-insensitive
    cleaned = cleaned.replace(/sucurdales\s+copec/gi, "");
    cleaned = cleaned.replace(/sucursales\s+copec/gi, "");
    cleaned = cleaned.replace(/sucursal\s+copec/gi, "");
    cleaned = cleaned.replace(/sucursales/gi, "");
    cleaned = cleaned.replace(/sucurdales/gi, "");
    
    // Clean up empty parentheses or brackets
    cleaned = cleaned.replace(/\(\s*\)/g, "");
    cleaned = cleaned.replace(/\[\s*\]/g, "");
    
    // Clean up surrounding punctuation or whitespace
    cleaned = cleaned.replace(/^\s*[-–—/()]\s*/, "").replace(/\s*[-–—/()]\s*$/, "");
    cleaned = cleaned.replace(/\s+/g, " ").trim();
    return cleaned || name;
  };

  const [selectedZones, setSelectedZones] = useState([]);
  const [hasInitializedZones, setHasInitializedZones] = useState(false);
  const [hasInitializedFacilities, setHasInitializedFacilities] = useState(false);

  // Sync selectedZones when allZones or isCopec loads/changes
  useEffect(() => {
    if (isCopec && allZones.length > 0 && !hasInitializedZones) {
      setSelectedZones(allZones);
      setHasInitializedZones(true);
    }
  }, [allZones, isCopec, hasInitializedZones]);

  // Sync selectedFacilityIds when facilitiesList loads/changes
  useEffect(() => {
    if (facilitiesList.length > 0 && !hasInitializedFacilities) {
      setSelectedFacilityIds(facilitiesList.map(f => f.id));
      setHasInitializedFacilities(true);
    }
  }, [facilitiesList, hasInitializedFacilities]);

  // Reset initialization when facility changes
  useEffect(() => {
    setHasInitializedFacilities(false);
    setHasInitializedZones(false);
    setSelectedFacilityIds([]);
    setSelectedZones([]);
    setSelectedHeatmapCell(null);
  }, [facility?.id]);

  // Load facilities for this supervisor reactively
  useEffect(() => {
    if (!user?.uid) {
      if (facility) {
        setFacilitiesList([facility]);
      }
      return;
    }

    const facilitiesRef = collection(db, "Facilities");
    const q = query(
      facilitiesRef,
      or(
        where("userUID", "==", user.uid),
        where("supervisorUID", "==", user.uid),
        where("devUID", "==", user.uid),
        where("chiefUID", "==", user.uid)
      )
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      if (list.length > 0) {
        setFacilitiesList(list);
      } else if (facility) {
        setFacilitiesList([facility]);
      }
    }, (error) => {
      console.error("Error loading facilities:", error);
      if (facility) {
        setFacilitiesList([facility]);
      }
    });

    return () => unsub();
  }, [user?.uid, facility]);

  const facilitiesListSerialized = JSON.stringify(facilitiesList.map(f => ({ id: f.id, title: f.title, company: f.company })));

  // Load zones for all facilities reactively
  useEffect(() => {
    if (facilitiesList.length === 0) {
      setAllZones([]);
      return;
    }

    const zonesMap = {};
    const unsubs = facilitiesList.map((fac) => {
      const facId = fac.id;
      const zonesRef = collection(db, `Facilities/${facId}/zones`);
      return onSnapshot(zonesRef, (snapshot) => {
        const docs = snapshot.docs.map(doc => ({
          id: doc.id,
          facilityId: facId,
          ...doc.data()
        }));
        zonesMap[facId] = docs;
        const merged = Object.values(zonesMap).flat();
        setAllZones(merged);
      }, (error) => {
        console.error(`Error loading zones for facility ${facId}:`, error);
      });
    });

    return () => {
      unsubs.forEach(unsub => unsub());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilitiesListSerialized]);

   const [period, setPeriod]   = useState("week");
  const [search, setSearch]   = useState("");
  const [minDurationFilter, setMinDurationFilter] = useState(0); // 0 to 1200 seconds (step 5)
  const [maxDurationFilter, setMaxDurationFilter] = useState(1200); // 0 to 1200 seconds (step 5)
  const [activeThumb, setActiveThumb] = useState(""); // "min" or "max"
  const [selectedHeatmapCell, setSelectedHeatmapCell] = useState(null); // { rowIdx, dayIdx }
  const [detail, setDetail]   = useState(null);
  const [filterResult, setFilterResult] = useState("all");

  // Settings Modal States
  const [showSettings, setShowSettings] = useState(false);
  const [hoursActive, setHoursActive] = useState(false);
  const [startHour, setStartHour] = useState(8);
  const [endHour, setEndHour] = useState(1);
  const [autoCloseActive, setAutoCloseActive] = useState(false);
  const [closeTime, setCloseTime] = useState(8);
  const [closeUnit, setCloseUnit] = useState("hours");
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // New Settings States
  const [mgmtAlertsActive, setMgmtAlertsActive] = useState(false);
  const [alertSecsDisp, setAlertSecsDisp] = useState(60);
  const [alertMinsClose, setAlertMinsClose] = useState(15);
  const [quietRules, setQuietRules] = useState([]);
  const [newRuleName, setNewRuleName] = useState("");
  const [newRuleStart, setNewRuleStart] = useState("09:58");
  const [newRuleEnd, setNewRuleEnd] = useState("10:02");
  const [teleChatIds, setTeleChatIds] = useState("");
  const [teleNotifyRelevant, setTeleNotifyRelevant] = useState(true);
  const [teleNotifyPersistent, setTeleNotifyPersistent] = useState(true);
  const [teleNotifyUnmanaged, setTeleNotifyUnmanaged] = useState(true);

  // UX/UI States
  const [activeModal, setActiveModal] = useState(null); // 'activity', 'zones', 'performance'
  // Orden y filtros del modal de historial ampliado
  const [histSort, setHistSort] = useState({ key: null, dir: "asc" });
  const [histFilters, setHistFilters] = useState({ relevant: false, findings: false, plate: false, suspect: false, unreviewed: false, validated: false });
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [modalReady, setModalReady] = useState(false);

  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" ? window.innerWidth >= 1024 : true);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (activeModal) {
      const timer = setTimeout(() => {
        setModalReady(true);
      }, 150);
      return () => clearTimeout(timer);
    } else {
      setModalReady(false);
    }
  }, [activeModal]);

  // Sync settings when modal opens or facility document changes
  useEffect(() => {
    if (facility && showSettings) {
      // 1. Shift range
      const range = facility.visibleHoursRange;
      if (Array.isArray(range) && range.length === 2) {
        setHoursActive(true);
        setStartHour(Number(range[0]));
        setEndHour(Number(range[1]));
      } else {
        setHoursActive(false);
        setStartHour(8);
        setEndHour(1);
      }

      // 2. Auto close
      setAutoCloseActive(!!facility.autoCloseAlarms);
      const seconds = facility.secondsToCloseAlarm || (8 * 3600);
      if (seconds % 3600 === 0) {
        setCloseTime(seconds / 3600);
        setCloseUnit("hours");
      } else {
        setCloseTime(Math.round(seconds / 60));
        setCloseUnit("minutes");
      }

      // 3. Management Alerts
      setMgmtAlertsActive(!!facility.managementAlertsActive);
      setAlertSecsDisp(facility.alertSecondsToDispatch || 60);
      setAlertMinsClose(facility.alertMinutesToClose || 15);

      // 4. Quiet Rules
      setQuietRules(Array.isArray(facility.quietRules) ? facility.quietRules : []);

      // 5. Telegram Config
      const tc = facility.telegramConfig || {};
      setTeleChatIds(tc.chatIds || "");
      setTeleNotifyRelevant(tc.notifyRelevant !== false);
      setTeleNotifyPersistent(tc.notifyPersistent !== false);
      setTeleNotifyUnmanaged(tc.notifyUnmanaged !== false);
    }
  }, [facility, showSettings]);

  const handleSaveSettings = async () => {
    if (!isSupervisor) return;
    setSaving(true);
    setErrorMsg("");

    try {
      const facilityRef = doc(db, `Facilities/${facility.id}`);
      const updates = {
        autoCloseAlarms: autoCloseActive,
        secondsToCloseAlarm: autoCloseActive
          ? (closeUnit === "hours" ? Number(closeTime) * 3600 : Number(closeTime) * 60)
          : (facility.secondsToCloseAlarm || 28800),
        managementAlertsActive: mgmtAlertsActive,
        alertSecondsToDispatch: Number(alertSecsDisp),
        alertMinutesToClose: Number(alertMinsClose),
        quietRules: quietRules,
        telegramConfig: {
          chatIds: teleChatIds,
          notifyRelevant: teleNotifyRelevant,
          notifyPersistent: teleNotifyPersistent,
          notifyUnmanaged: teleNotifyUnmanaged
        }
      };

      if (hoursActive) {
        updates.visibleHoursRange = [Number(startHour), Number(endHour)];
      } else {
        updates.visibleHoursRange = null;
      }

      await updateDoc(facilityRef, updates);
      setShowSettings(false);
    } catch (err) {
      console.error("Error saving facility settings:", err);
      setErrorMsg("Error al guardar los cambios.");
    } finally {
      setSaving(false);
    }
  };
  
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(true);

  // Derive activeZones using allZones or prop zones fallback
  const activeZones = useMemo(() => {
    return allZones.length > 0 ? allZones : (zones || []);
  }, [allZones, zones]);

  // Derive zonesIds dynamically from activeZones (listen to all zones for the current facilities)
  const zonesIds = useMemo(() => {
    const rawZonesIds = activeZones.reduce((acc, z) => {
      if (schema.numericIds) {
        const num = Number(z.id);
        if (!isNaN(num)) acc.push(num);
        acc.push(String(z.id));
      } else {
        acc.push(z.id);
      }
      return acc;
    }, []);
    return [...new Set(rawZonesIds)];
  }, [activeZones]);

  // Serialize dependencies to prevent continuous re-subscription infinite loops
  const zonesIdsSerialized = JSON.stringify(zonesIds);
  const activeZonesSerialized = JSON.stringify(activeZones.map(z => ({ id: z.id, zoneName: z.zoneName, facilityId: z.facilityId })));
  const facilitySerialized = JSON.stringify(facility ? { id: facility.id, title: facility.title, visibleHoursRange: facility.visibleHoursRange } : null);

  useEffect(() => {
    const isMockData = window.location.search.includes("mockData=true");
    if (isMockData) {
      setAlarms(generateMockAlarms(zones));
      setLoading(false);
      return;
    }
    if (activeZones.length > 0 && zonesIds.length > 0) {
      setLoading(true);
      const alarmsRef = collection(db, schema.groupedAlarms);
      
      const chunkedZones = [];
      for (let i = 0; i < zonesIds.length; i += 30) {
        chunkedZones.push(zonesIds.slice(i, i + 30));
      }

      const chunksData = Array.from({ length: chunkedZones.length }, () => []);

      const unsubs = chunkedZones.map((chunk, index) => {
        const q = query(
          alarmsRef,
          where(schema.zoneField, "in", chunk),
          orderBy("updatedAt", "desc"),
          limit(2000)
        );

        return onSnapshot(q, (snapshot) => {
          const formattedAlarms = snapshot.docs.map(doc => {
            const lastDate = doc.data().resolvedAt ? new Date(doc.data().resolvedAt.seconds * 1000) : new Date();
            const zoneObj = activeZones.find((f) => String(f.id) === String(doc.data()[schema.zoneField]));
            const facId = zoneObj?.facilityId || facility?.id;
            const facName = facilitiesList.find(f => f.id === facId)?.title || facility?.title || "";
            const zName = zoneObj?.zoneName || `Zona ${doc.data()[schema.zoneField]}`;
            const fullZoneName = isCopec ? cleanCopecName(zName) : (facName ? `${facName} - ${zName}` : zName);

            return {
              ...doc.data(),
              lastDate,
              resolvedAt: doc.data().resolvedAt,
              date: new Date((doc.data().updatedAt?.seconds || 0) * 1000),
              zoneName: fullZoneName,
              docID: doc.id,
              createdAtLocalTime: moment((doc.data().createdAt?.seconds || 0) * 1000).format('LT'),
              createdAtDate: new Date((doc.data().createdAt?.seconds || 0) * 1000),
            };
          });

          chunksData[index] = formattedAlarms;

          const mergedAlarms = chunksData.flat().sort((a, b) => {
            const timeA = a.updatedAt?.seconds || 0;
            const timeB = b.updatedAt?.seconds || 0;
            return timeB - timeA;
          }).slice(0, 2000);

          const isAlarmInTime = alarmHour => {
            if (!facility?.visibleHoursRange) return true;
            const [start, end] = facility.visibleHoursRange;
            if (start <= end) {
              return alarmHour >= start && alarmHour < end;
            } else {
              return alarmHour >= start || alarmHour < end;
            }
          };

          const filteredFormattedAlarms = facility?.visibleHoursRange === undefined ? mergedAlarms : mergedAlarms.filter(alarm => isAlarmInTime(alarm.createdAtDate.getHours()));

          setAlarms(filteredFormattedAlarms);
          setLoading(false);
        }, (error) => {
          console.error("Supervisor Alarms load error:", error);
          setLoading(false);
        });
      });
      return () => unsubs.forEach(unsub => unsub());
    } else {
      setAlarms([]);
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zonesIdsSerialized, activeZonesSerialized, facilitiesListSerialized, facilitySerialized, isCopec, zones]);

  const periodMs = { day: 86400000, week: 604800000, month: 2592000000 }[period];

  // Filter alarms in memory by selected recintos/zones to avoid restarting Firestore subscriptions
  const filteredAlarmsByRecinto = useMemo(() => {
    if (isCopec) {
      return alarms.filter(a => selectedZones.some(sz => String(sz.id) === String(a[schema.zoneField])));
    } else {
      return alarms.filter(a => {
        const zoneObj = activeZones.find(az => String(az.id) === String(a[schema.zoneField]));
        const facId = zoneObj?.facilityId || facility?.id;
        return selectedFacilityIds.includes(facId);
      });
    }
  }, [alarms, isCopec, selectedZones, activeZones, selectedFacilityIds, facility?.id]);

  const baseFiltered = useMemo(() => {
    const createdMs = a => a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
    let list = filteredAlarmsByRecinto.filter(a => (Date.now() - createdMs(a)) < periodMs);
    
    if (filterResult !== "all") {
      list = list.filter(a => {
        const isClosed = !!a.resolvedAt;
        const res = normalizeResult(a.closeResult || a.reasonForResolution);
        const situation = String(a.situation || "");

        const isDrill = res === "drill" || (!res && situation === "1");
        const hasFindings = res === "with_findings" || (!res && situation === "6");
        const isUnmanaged = !isClosed || res === "unmanaged" || (!res && situation === "2") || a.closedBy === "Sistema";
        const isWithoutFindings = res === "without_findings" || (!res && ["3", "4", "5", "7"].includes(situation));

        if (filterResult === "drill") return isDrill;
        if (filterResult === "with_findings") return hasFindings;
        if (filterResult === "without_findings") return isWithoutFindings && !isDrill && !isUnmanaged;
        if (filterResult === "unmanaged") return isUnmanaged;
        return true;
      });
    }

    if (search) {
      const s = search.toLowerCase();
      list = list.filter(a =>
        a.zoneName?.toLowerCase().includes(s) ||
        a.docID?.toLowerCase().includes(s) ||
        a.dispatchedBy?.toLowerCase().includes(s) ||
        a.guardName?.toLowerCase().includes(s) ||
        a.guards?.some(g => g.toLowerCase().includes(s)) ||
        a.closedBy?.toLowerCase().includes(s) ||
        a.plate?.toLowerCase().includes(s) ||
        a.vehicle?.plate?.toLowerCase().includes(s)
      );
    }
    return list.sort((a, b) => {
      const ta = a.createdAt?.seconds || 0;
      const tb = b.createdAt?.seconds || 0;
      return tb - ta;
    });
  }, [filteredAlarmsByRecinto, periodMs, search, filterResult]);

  const durationFiltered = useMemo(() => {
    return baseFiltered.filter(a => {
      let durSecs = 0;
      if (a.duration != null) {
        durSecs = a.duration;
      } else if (a.resolvedAt?.seconds && a.createdAt?.seconds) {
        durSecs = a.resolvedAt.seconds - a.createdAt.seconds;
      }
      durSecs = Math.max(0, durSecs);

      const matchesMin = minDurationFilter === 0 || durSecs > minDurationFilter;
      const matchesMax = maxDurationFilter === 1200 || durSecs <= maxDurationFilter;
      return matchesMin && matchesMax;
    });
  }, [baseFiltered, minDurationFilter, maxDurationFilter]);

  const filtered = useMemo(() => {
    if (!selectedHeatmapCell) return durationFiltered;
    return durationFiltered.filter(a => {
      const t = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
      if (!t) return false;
      const m = moment(t);
      const hour = m.hour();
      const dayVal = m.day(); // 0 = Sunday, 1 = Monday, ...
      const dayIdx = (dayVal + 6) % 7; // Monday = 0, Sunday = 6
      const rowIdx = Math.floor(hour / 3);
      return rowIdx === selectedHeatmapCell.rowIdx && dayIdx === selectedHeatmapCell.dayIdx;
    });
  }, [durationFiltered, selectedHeatmapCell]);

  const closed     = filtered.filter(a => a.resolvedAt);

  // Pendientes de revisión en el periodo seleccionado (cerrados sin reviewStatus)
  const pendingReviewCount = useMemo(
    () => filtered.filter((a) => a.resolvedAt && !a.reviewStatus).length,
    [filtered]
  );

  const chartData = useMemo(() => {
    const list = [];
    if (period === "day") {
      for (let i = 23; i >= 0; i--) {
        const time = moment().subtract(i, "hours");
        list.push({
          label: time.format("HH:00"),
          timestamp: time.startOf("hour").valueOf(),
          Total: 0,
          Gestionados: 0,
          ConHallazgos: 0,
        });
      }
      filtered.forEach(a => {
        const t = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
        if (!t) return;
        const alarmTime = moment(t);
        const bucket = list.find(b => alarmTime.isSame(b.timestamp, "hour"));
        if (bucket) {
          bucket.Total++;
          if (a.resolvedAt && a.closedBy && a.closedBy !== "Sistema") {
            bucket.Gestionados++;
          }
          const r = normalizeResult(a.closeResult || a.reasonForResolution);
          if (r === "with_findings" || String(a.situation || "") === "6") {
            bucket.ConHallazgos++;
          }
        }
      });
    } else if (period === "week") {
      for (let i = 6; i >= 0; i--) {
        const time = moment().subtract(i, "days");
        list.push({
          label: time.format("ddd DD"),
          timestamp: time.startOf("day").valueOf(),
          Total: 0,
          Gestionados: 0,
          ConHallazgos: 0,
        });
      }
      filtered.forEach(a => {
        const t = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
        if (!t) return;
        const alarmTime = moment(t);
        const bucket = list.find(b => alarmTime.isSame(b.timestamp, "day"));
        if (bucket) {
          bucket.Total++;
          if (a.resolvedAt && a.closedBy && a.closedBy !== "Sistema") {
            bucket.Gestionados++;
          }
          const r = normalizeResult(a.closeResult || a.reasonForResolution);
          if (r === "with_findings" || String(a.situation || "") === "6") {
            bucket.ConHallazgos++;
          }
        }
      });
    } else {
      for (let i = 29; i >= 0; i--) {
        const time = moment().subtract(i, "days");
        list.push({
          label: time.format("DD MMM"),
          timestamp: time.startOf("day").valueOf(),
          Total: 0,
          Gestionados: 0,
          ConHallazgos: 0,
        });
      }
      filtered.forEach(a => {
        const t = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
        if (!t) return;
        const alarmTime = moment(t);
        const bucket = list.find(b => alarmTime.isSame(b.timestamp, "day"));
        if (bucket) {
          bucket.Total++;
          if (a.resolvedAt && a.closedBy && a.closedBy !== "Sistema") {
            bucket.Gestionados++;
          }
          const r = normalizeResult(a.closeResult || a.reasonForResolution);
          if (r === "with_findings" || String(a.situation || "") === "6") {
            bucket.ConHallazgos++;
          }
        }
      });
    }
    return list;
  }, [filtered, period]);

  // Tasa de gestión: eventos gestionados por humano / total eventos (excluyendo simulacros)
  const isDrill = a => normalizeResult(a.closeResult || a.reasonForResolution) === "drill";
  const totalNoDrill   = filtered.filter(a => !isDrill(a)).length;
  const managedByHuman = closed.filter(a => !isDrill(a) && a.closedBy && a.closedBy !== "Sistema").length;
  const managedPct     = totalNoDrill > 0 ? Math.round(managedByHuman / totalNoDrill * 100) : 0;

  const byResult = {};
  closed.forEach(a => {
    const r = normalizeResult(a.closeResult || a.reasonForResolution);
    if (r) byResult[r] = (byResult[r] || 0) + 1;
  });

  const avgDisp = (() => {
    const t = closed
      .filter(a => a.dispatchTime && a.createdAt?.seconds)
      .map(a => Math.max(0, (getMillis(a.dispatchTime) - a.createdAt.seconds * 1000) / 1000));
    return t.length ? Math.round(t.reduce((a, b) => a + b, 0) / t.length) : 0;
  })();

  const avgArr = (() => {
    const t = closed
      .filter(a => a.arrivedTime && a.dispatchTime)
      .map(a => Math.max(0, (getMillis(a.arrivedTime) - getMillis(a.dispatchTime)) / 1000));
    return t.length ? Math.round(t.reduce((a, b) => a + b, 0) / t.length) : 0;
  })();

  // KPI #4 – Gestión con hallazgos (% of closed events with findings)
  const withFindings = byResult.with_findings || 0;
  const findingsPct  = closed.length ? Math.round(withFindings / closed.length * 100) : 0;

  // KPI #5 – Nivel de actividad (accumulated minutes in current period)
  const toMinutes = (a) => {
    if (a.duration != null) return Math.max(0, a.duration / 60);
    if (a.resolvedAt?.seconds && a.createdAt?.seconds)
      return Math.max(0, (a.resolvedAt.seconds - a.createdAt.seconds) / 60);
    return 0;
  };
  const currentMinutes = Math.round(filtered.reduce((sum, a) => sum + toMinutes(a), 0));

  // Historical average: last 4 periods of the same length (excludes current period)
  const histAvgMinutes = (() => {
    const totals = [];
    for (let i = 1; i <= 4; i++) {
      const start = Date.now() - periodMs * (i + 1);
      const end   = Date.now() - periodMs * i;
      const slice = filteredAlarmsByRecinto.filter(a => {
        const ms = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
        return ms >= start && ms < end;
      });
      totals.push(slice.reduce((sum, a) => sum + toMinutes(a), 0));
    }
    const avg = totals.reduce((s, v) => s + v, 0) / totals.length;
    return Math.round(avg);
  })();

  const activityDelta = histAvgMinutes > 0
    ? Math.round((currentMinutes - histAvgMinutes) / histAvgMinutes * 100)
    : null;
  const activityLevel = (() => {
    if (histAvgMinutes === 0) return { label: "N/A", color: "#64748b" };
    if (activityDelta <= -25) return { label: "BAJO",   color: "#22c55e" };
    if (activityDelta <= 25)  return { label: "NORMAL", color: "#3b82f6" };
    if (activityDelta <= 75)  return { label: "ALTO",   color: "#f59e0b" };
    return                           { label: "MUY ALTO", color: "#ef4444" };
  })();

  const activitySub = histAvgMinutes > 0
    ? `${currentMinutes} min totales · ${activityDelta !== null ? (activityDelta >= 0 ? "+" : "") + activityDelta + "% vs prom. 4 semanas (" + histAvgMinutes + " min)" : ""}`
    : `${currentMinutes} min totales · sin historial`;

  // KPI #6 – Zona crítica (zone with most accumulated minutes)
  const minutesByZone = {};
  filtered.forEach(a => {
    const zone = a.zoneName || "Sin zona";
    minutesByZone[zone] = (minutesByZone[zone] || 0) + toMinutes(a);
  });
  const criticalZoneEntry = Object.entries(minutesByZone).sort((a, b) => b[1] - a[1])[0];
  const criticalZone    = criticalZoneEntry ? criticalZoneEntry[0] : null;
  const criticalMins    = criticalZoneEntry ? Math.round(criticalZoneEntry[1]) : 0;
  const criticalPct     = currentMinutes > 0 && criticalMins > 0
    ? Math.round(criticalMins / currentMinutes * 100)
    : 0;

  // Previous period filtering for comparison
  const prevPeriodFiltered = useMemo(() => {
    let list = filteredAlarmsByRecinto;
    if (periodMs) {
      const now = Date.now();
      const startMs = now - 2 * periodMs;
      const endMs = now - periodMs;
      list = list.filter(a => {
        const ms = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
        return ms >= startMs && ms < endMs;
      });
    } else {
      return [];
    }

    // Apply duration filter to previous period
    const listFiltered = list.filter(a => {
      let durSecs = 0;
      if (a.duration != null) {
        durSecs = a.duration;
      } else if (a.resolvedAt?.seconds && a.createdAt?.seconds) {
        durSecs = a.resolvedAt.seconds - a.createdAt.seconds;
      }
      durSecs = Math.max(0, durSecs);

      const matchesMin = minDurationFilter === 0 || durSecs > minDurationFilter;
      const matchesMax = maxDurationFilter === 1200 || durSecs <= maxDurationFilter;
      return matchesMin && matchesMax;
    });

    if (!selectedHeatmapCell) return listFiltered;

    return listFiltered.filter(a => {
      const t = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
      if (!t) return false;
      const m = moment(t);
      const hour = m.hour();
      const dayVal = m.day();
      const dayIdx = (dayVal + 6) % 7;
      const rowIdx = Math.floor(hour / 3);
      return rowIdx === selectedHeatmapCell.rowIdx && dayIdx === selectedHeatmapCell.dayIdx;
    });
  }, [filteredAlarmsByRecinto, periodMs, minDurationFilter, maxDurationFilter, selectedHeatmapCell]);

  const prevStats = useMemo(() => {
    const closedPrev = prevPeriodFiltered.filter(a => a.resolvedAt);

    // 1. Tasa de gestión anterior
    const isDrill = a => normalizeResult(a.closeResult || a.reasonForResolution) === "drill";
    const prevTotalNoDrill = prevPeriodFiltered.filter(a => !isDrill(a)).length;
    const prevManagedByHuman = closedPrev.filter(a => !isDrill(a) && a.closedBy && a.closedBy !== "Sistema").length;
    const prevManagedPct = prevTotalNoDrill > 0 ? Math.round(prevManagedByHuman / prevTotalNoDrill * 100) : 0;

    // 2. Despacho CCTV anterior
    const prevAvgDisp = (() => {
      const t = closedPrev
        .filter(a => a.dispatchTime && a.createdAt?.seconds)
        .map(a => Math.max(0, (getMillis(a.dispatchTime) - a.createdAt.seconds * 1000) / 1000));
      return t.length ? Math.round(t.reduce((a, b) => a + b, 0) / t.length) : 0;
    })();

    // 3. Llegada guardia anterior
    const prevAvgArr = (() => {
      const t = closedPrev
        .filter(a => a.arrivedTime && a.dispatchTime)
        .map(a => Math.max(0, (getMillis(a.arrivedTime) - getMillis(a.dispatchTime)) / 1000));
      return t.length ? Math.round(t.reduce((a, b) => a + b, 0) / t.length) : 0;
    })();

    // 4. Gestión con hallazgos anterior
    const prevWithFindings = closedPrev.filter(a => {
      const r = normalizeResult(a.closeResult || a.reasonForResolution);
      return r === "with_findings" || String(a.situation || "") === "6";
    }).length;
    const prevFindingsPct = closedPrev.length ? Math.round(prevWithFindings / closedPrev.length * 100) : 0;

    // 5. Minutos de actividad anterior
    const toMinutesLocal = (a) => {
      if (a.duration != null) return Math.max(0, a.duration / 60);
      if (a.resolvedAt?.seconds && a.createdAt?.seconds)
        return Math.max(0, (a.resolvedAt.seconds - a.createdAt.seconds) / 60);
      return 0;
    };
    const prevMinutesTotal = Math.round(prevPeriodFiltered.reduce((sum, a) => sum + toMinutesLocal(a), 0));

    return {
      managedPct: prevManagedPct,
      avgDisp: prevAvgDisp,
      avgArr: prevAvgArr,
      findingsPct: prevFindingsPct,
      minutesTotal: prevMinutesTotal
    };
  }, [prevPeriodFiltered]);

  // Heatmap Data (Days of week Lunes-Domingo vs Hour blocks of 3 hours)
  const heatmapData = useMemo(() => {
    const grid = Array.from({ length: 8 }, (_, r) => {
      const startHour = r * 3;
      const endHour = startHour + 3;
      return {
        rowIdx: r,
        label: `${String(startHour).padStart(2, "0")}-${String(endHour).padStart(2, "0")}`,
        days: Array(7).fill(0)
      };
    });

    durationFiltered.forEach(a => {
      const t = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
      if (!t) return;
      const m = moment(t);
      const hour = m.hour();
      const dayVal = m.day(); // 0 = Sunday, 1 = Monday, ...
      const dayIdx = (dayVal + 6) % 7; // Monday = 0, Sunday = 6
      const rowIdx = Math.floor(hour / 3);

      if (grid[rowIdx] && dayIdx >= 0 && dayIdx < 7) {
        const dur = a.duration || 0;
        grid[rowIdx].days[dayIdx] += dur;
      }
    });

    let maxVal = 0;
    grid.forEach(row => {
      row.days.forEach(val => {
        if (val > maxVal) maxVal = val;
      });
    });

    return { grid, maxVal };
  }, [durationFiltered]);

  const fmtHeatVal = (seconds) => {
    if (!seconds) return "";
    const mins = Math.round(seconds / 60);
    if (mins < 60) return `${mins}m`;
    const hrs = (mins / 60).toFixed(1);
    return `${hrs}h`;
  };

  // Top 15 critical zones by count
  const alertsByZone = useMemo(() => {
    const counts = {};
    filtered.forEach(a => {
      const zone = a.zoneName || "Sin zona";
      counts[zone] = (counts[zone] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 15);
  }, [filtered]);

  const pieData = useMemo(() => {
    return Object.entries(byResult).map(([key, value]) => ({
      name: R_LABELS[key] || key,
      value,
      key
    }));
  }, [byResult]);

  // Previous period chart data for activity trend comparison
  const prevChartData = useMemo(() => {
    const list = [];
    if (period === "day") {
      for (let i = 23; i >= 0; i--) {
        const time = moment().subtract(i, "hours");
        list.push({
          label: time.format("HH:00"),
          timestamp: moment(time).subtract(1, "day").startOf("hour").valueOf(),
          TotalPrev: 0,
          GestionadosPrev: 0,
        });
      }
      prevPeriodFiltered.forEach(a => {
        const t = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
        if (!t) return;
        const alarmTime = moment(t);
        const hourLabel = alarmTime.format("HH:00");
        const bucket = list.find(b => b.label === hourLabel);
        if (bucket) {
          bucket.TotalPrev++;
          if (a.resolvedAt && a.closedBy && a.closedBy !== "Sistema") {
            bucket.GestionadosPrev++;
          }
        }
      });
    } else if (period === "week") {
      for (let i = 6; i >= 0; i--) {
        const time = moment().subtract(i, "days");
        list.push({
          label: time.format("ddd DD"),
          dayOfWeek: time.day(),
          TotalPrev: 0,
          GestionadosPrev: 0,
        });
      }
      prevPeriodFiltered.forEach(a => {
        const t = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
        if (!t) return;
        const alarmTime = moment(t);
        const dow = alarmTime.day();
        const bucket = list.find(b => b.dayOfWeek === dow);
        if (bucket) {
          bucket.TotalPrev++;
          if (a.resolvedAt && a.closedBy && a.closedBy !== "Sistema") {
            bucket.GestionadosPrev++;
          }
        }
      });
    } else {
      for (let i = 29; i >= 0; i--) {
        const time = moment().subtract(i, "days");
        list.push({
          label: time.format("DD MMM"),
          dayOfMonth: time.date(),
          TotalPrev: 0,
          GestionadosPrev: 0,
        });
      }
      prevPeriodFiltered.forEach(a => {
        const t = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
        if (!t) return;
        const alarmTime = moment(t);
        const dom = alarmTime.date();
        const bucket = list.find(b => b.dayOfMonth === dom);
        if (bucket) {
          bucket.TotalPrev++;
          if (a.resolvedAt && a.closedBy && a.closedBy !== "Sistema") {
            bucket.GestionadosPrev++;
          }
        }
      });
    }
    return list;
  }, [prevPeriodFiltered, period]);

  // Merged chart data: current + previous period overlay
  const mergedChartData = useMemo(() => {
    return chartData.map((d, i) => ({
      ...d,
      TotalPrev: prevChartData[i]?.TotalPrev || 0,
      GestionadosPrev: prevChartData[i]?.GestionadosPrev || 0,
    }));
  }, [chartData, prevChartData]);

  // Findings chart data for "Gestión con hallazgos" modal
  const findingsChartData = useMemo(() => {
    const list = [];
    const closedFiltered = filtered.filter(a => a.resolvedAt);
    if (period === "day") {
      for (let i = 23; i >= 0; i--) {
        const time = moment().subtract(i, "hours");
        list.push({ label: time.format("HH:00"), timestamp: time.startOf("hour").valueOf(), ConHallazgos: 0, Total: 0 });
      }
      closedFiltered.forEach(a => {
        const t = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
        if (!t) return;
        const alarmTime = moment(t);
        const bucket = list.find(b => alarmTime.isSame(b.timestamp, "hour"));
        if (bucket) {
          bucket.Total++;
          const r = normalizeResult(a.closeResult || a.reasonForResolution);
          if (r === "with_findings" || String(a.situation || "") === "6") bucket.ConHallazgos++;
        }
      });
    } else if (period === "week") {
      for (let i = 6; i >= 0; i--) {
        const time = moment().subtract(i, "days");
        list.push({ label: time.format("ddd DD"), timestamp: time.startOf("day").valueOf(), ConHallazgos: 0, Total: 0 });
      }
      closedFiltered.forEach(a => {
        const t = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
        if (!t) return;
        const alarmTime = moment(t);
        const bucket = list.find(b => alarmTime.isSame(b.timestamp, "day"));
        if (bucket) {
          bucket.Total++;
          const r = normalizeResult(a.closeResult || a.reasonForResolution);
          if (r === "with_findings" || String(a.situation || "") === "6") bucket.ConHallazgos++;
        }
      });
    } else {
      for (let i = 29; i >= 0; i--) {
        const time = moment().subtract(i, "days");
        list.push({ label: time.format("DD MMM"), timestamp: time.startOf("day").valueOf(), ConHallazgos: 0, Total: 0 });
      }
      closedFiltered.forEach(a => {
        const t = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
        if (!t) return;
        const alarmTime = moment(t);
        const bucket = list.find(b => alarmTime.isSame(b.timestamp, "day"));
        if (bucket) {
          bucket.Total++;
          const r = normalizeResult(a.closeResult || a.reasonForResolution);
          if (r === "with_findings" || String(a.situation || "") === "6") bucket.ConHallazgos++;
        }
      });
    }
    return list;
  }, [filtered, period]);

  // Guard stats
  const guardStats = {};
  closed.forEach(a => {
    const gs = Array.isArray(a.guards) ? a.guards : a.guardName ? [a.guardName] : [];
    gs.forEach(g => {
      if (!guardStats[g]) guardStats[g] = { ev: 0, arr: [], bars: [], ok: 0 };
      guardStats[g].ev++;
      if (a.arrivedTime && a.dispatchTime) guardStats[g].arr.push(Math.max(0, (getMillis(a.arrivedTime) - getMillis(a.dispatchTime)) / 1000));
      const nb = a.signalBarsNum ?? (Number(a.signalBars) || null);
      if (nb) guardStats[g].bars.push(nb);
      if (normalizeResult(a.closeResult || a.reasonForResolution) === "with_findings") guardStats[g].ok++;
    });
  });

  // Operator stats
  const opStats = {};
  filtered.forEach(a => {
    const dispatcher = a.dispatchedBy || a.guardName;
    if (dispatcher) {
      if (!opStats[dispatcher]) opStats[dispatcher] = { disp: 0, close: 0, ok: 0, reactionTimes: [] };
      opStats[dispatcher].disp++;
      if (a.dispatchTime && a.createdAt) {
        const diffSeconds = Math.max(0, (getMillis(a.dispatchTime) - getMillis(a.createdAt)) / 1000);
        opStats[dispatcher].reactionTimes.push(diffSeconds);
      }
    }
    const closer = a.closedBy;
    if (closer) {
      if (!opStats[closer]) opStats[closer] = { disp: 0, close: 0, ok: 0, reactionTimes: [] };
      opStats[closer].close++;
      if (normalizeResult(a.closeResult || a.reasonForResolution) === "with_findings") opStats[closer].ok++;
    }
  });

  const handleExportCSV = (exportRows) => {
    const data = Array.isArray(exportRows) ? exportRows : filtered;
    if (!data || !data.length) return;
    const headers = [
      "ID Documento",
      "Zona",
      "Fecha de Alarma",
      "Fecha de Despacho",
      "Tiempo Despacho (seg)",
      "Fecha de Llegada Guardia",
      "Tiempo Respuesta Guardia (seg)",
      "Fecha de Cierre",
      "Tiempo Total Alerta (seg)",
      "Despachado Por (CCTV)",
      "Cerrado Por",
      "Guardias Despachados",
      "Resultado",
      "Situación",
      "Señal (Barras)",
      "Patente",
      "Sospechoso",
      "Comentarios de Cierre"
    ];
    const rows = data.map(a => {
      const startMs = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : null;
      const dateStr = startMs ? new Date(startMs).toLocaleString("es-CL") : "—";
      
      const dispatchMs = a.dispatchTime?.seconds ? a.dispatchTime.seconds * 1000 : null;
      const dispatchStr = dispatchMs ? new Date(dispatchMs).toLocaleString("es-CL") : "—";
      const dispatchTimeSec = a.dispatchTime && a.createdAt ? Math.max(0, Math.round((getMillis(a.dispatchTime) - getMillis(a.createdAt)) / 1000)) : "—";
      
      const arrivedMs = a.arrivedTime?.seconds ? a.arrivedTime.seconds * 1000 : null;
      const arrivedStr = arrivedMs ? new Date(arrivedMs).toLocaleString("es-CL") : "—";
      const responseTimeSec = a.arrivedTime && a.dispatchTime ? Math.max(0, Math.round((getMillis(a.arrivedTime) - getMillis(a.dispatchTime)) / 1000)) : "—";
      
      const resolvedMs = a.resolvedAt?.seconds ? a.resolvedAt.seconds * 1000 : null;
      const resolvedStr = resolvedMs ? new Date(resolvedMs).toLocaleString("es-CL") : "—";
      
      const guardsStr = (Array.isArray(a.guards) ? a.guards.join(", ") : a.guardName) || "—";
      const label = a.resolvedAt ? getResultLabel(a) : "ABIERTO";
      
      const situationStr = SITUATIONS_MAP[a.situation] || a.situation || "—";
      
      const signalVal = a.signalBarsNum ?? (a.signalBars && a.signalBars !== "not_specified" ? Number(a.signalBars) : null);
      const signalStr = signalVal != null ? `${signalVal} de 5` : "—";
      
      const plateStr = a.vehicle?.plate || a.plate || "—";
      const suspectStr = a.suspectDesc || (a.suspect ? "Sí" : "—");
      
      const commentsStr = a.comments || a.notes || "—";

      return [
        a.docID || "—",
        a.zoneName || "—",
        dateStr,
        dispatchStr,
        dispatchTimeSec,
        arrivedStr,
        responseTimeSec,
        resolvedStr,
        a.duration != null ? a.duration : "—",
        a.dispatchedBy || "—",
        a.closedBy || "—",
        guardsStr,
        label,
        situationStr,
        signalStr,
        plateStr,
        suspectStr,
        commentsStr
      ];
    });

    const csvContent = [
      headers.join(";"),
      ...rows.map(r => r.map(val => `"${String(val).replace(/"/g, '""')}"`).join(";"))
    ].join("\n");

    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    const dateStr = new Date().toISOString().split('T')[0];
    link.setAttribute("download", `reporte_eventos_${facility?.name || "recinto"}_${dateStr}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = async () => {
    setSaving(true);
    try {
      const { jsPDF } = await import("jspdf");
      const html2canvas = (await import("html2canvas")).default;

      // Add print styles class to body
      document.body.classList.add("exporting-pdf");

      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4"
      });

      // Slide 1: KPIs & Charts (Trend & Distribution)
      const slide1 = document.getElementById("pdf-slide-1");
      if (slide1) {
        document.body.classList.add("exporting-slide-1");
        // Delay to allow Recharts to redraw and DOM layout to stabilize
        await new Promise(resolve => setTimeout(resolve, 350));
        const canvas1 = await html2canvas(slide1, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#070a13",
          logging: false
        });
        const imgData1 = canvas1.toDataURL("image/png");
        pdf.addImage(imgData1, "PNG", 0, 0, 297, 210, undefined, "FAST");
        document.body.classList.remove("exporting-slide-1");
      }

      // Slide 2: Analytics (Heatmap & Top Zones)
      const slide2 = document.getElementById("pdf-slide-2");
      if (slide2) {
        document.body.classList.add("exporting-slide-2");
        // Delay to allow Recharts and Heatmap grid to draw
        await new Promise(resolve => setTimeout(resolve, 350));
        pdf.addPage();
        const canvas2 = await html2canvas(slide2, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#070a13",
          logging: false
        });
        const imgData2 = canvas2.toDataURL("image/png");
        pdf.addImage(imgData2, "PNG", 0, 0, 297, 210, undefined, "FAST");
        document.body.classList.remove("exporting-slide-2");
      }

      // Slide 3: Performance tables
      const slide3 = document.getElementById("pdf-slide-3");
      if (slide3) {
        document.body.classList.add("exporting-slide-3");
        await new Promise(resolve => setTimeout(resolve, 350));
        pdf.addPage();
        const canvas3 = await html2canvas(slide3, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#070a13",
          logging: false
        });
        const imgData3 = canvas3.toDataURL("image/png");
        pdf.addImage(imgData3, "PNG", 0, 0, 297, 210, undefined, "FAST");
        document.body.classList.remove("exporting-slide-3");
      }

      // Slide 4: Event history with findings (only for PDF)
      const slide4 = document.getElementById("pdf-slide-4");
      if (slide4) {
        document.body.classList.add("exporting-slide-4");
        await new Promise(resolve => setTimeout(resolve, 350));
        pdf.addPage();
        const canvas4 = await html2canvas(slide4, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#070a13",
          logging: false
        });
        const imgData4 = canvas4.toDataURL("image/png");
        pdf.addImage(imgData4, "PNG", 0, 0, 297, 210, undefined, "FAST");
        document.body.classList.remove("exporting-slide-4");
      }

      const dateStr = new Date().toISOString().split('T')[0];
      pdf.save(`reporte_jammer_${facility?.name || "recinto"}_${dateStr}.pdf`);
    } catch (err) {
      console.error("Error generating PDF:", err);
    } finally {
      document.body.classList.remove("exporting-pdf", "exporting-slide-1", "exporting-slide-2", "exporting-slide-3", "exporting-slide-4");
      setSaving(false);
    }
  };

  const handlePrintPaper = () => {
    document.body.classList.add("print-paper");
    setTimeout(() => {
      window.print();
      document.body.classList.remove("print-paper");
    }, 150);
  };

  const periodLabel = { day: "hoy", week: "esta semana", month: "este mes" }[period];

  // Performance indicators for Slide 3
  const avgReactionTime = useMemo(() => {
    const times = [];
    Object.values(opStats).forEach(s => {
      if (s.reactionTimes) times.push(...s.reactionTimes);
    });
    return times.length ? Math.round(times.reduce((a, b) => a + b, 0) / times.length) : null;
  }, [opStats]);

  const avgArrivalTime = useMemo(() => {
    const times = [];
    Object.values(guardStats).forEach(s => {
      if (s.arr) times.push(...s.arr);
    });
    return times.length ? Math.round(times.reduce((a, b) => a + b, 0) / times.length) : null;
  }, [guardStats]);

  const bestOperator = useMemo(() => {
    let name = "N/A";
    let minTime = Infinity;
    Object.entries(opStats).forEach(([n, s]) => {
      if (s.reactionTimes && s.reactionTimes.length > 0) {
        const avg = s.reactionTimes.reduce((a, b) => a + b, 0) / s.reactionTimes.length;
        if (avg < minTime && s.disp > 0) {
          minTime = avg;
          name = n;
        }
      }
    });
    return name !== "N/A" ? `${name} (${fmtDur(Math.round(minTime))})` : "Sin datos";
  }, [opStats]);

  const bestGuard = useMemo(() => {
    let name = "N/A";
    let minTime = Infinity;
    Object.entries(guardStats).forEach(([n, s]) => {
      if (s.arr && s.arr.length > 0) {
        const avg = s.arr.reduce((a, b) => a + b, 0) / s.arr.length;
        if (avg < minTime && s.ev > 0) {
          minTime = avg;
          name = n;
        }
      }
    });
    return name !== "N/A" ? `${name} (${fmtDur(Math.round(minTime))})` : "Sin datos";
  }, [guardStats]);

  // Guardia con más asistencias (mayor nº de eventos atendidos)
  const topGuardByAttendance = useMemo(() => {
    let name = null, max = 0;
    Object.entries(guardStats).forEach(([n, s]) => {
      if (s.ev > max) { max = s.ev; name = n; }
    });
    return name ? { name, count: max } : null;
  }, [guardStats]);

  // Guardia con más hallazgos (eventos con resultado "Exit." / con hallazgo)
  const topGuardByFindings = useMemo(() => {
    let name = null, max = 0;
    Object.entries(guardStats).forEach(([n, s]) => {
      if (s.ok > max) { max = s.ok; name = n; }
    });
    return name ? { name, count: max } : null;
  }, [guardStats]);

  const toggleHistSort = (key) =>
    setHistSort(s => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  // Filas del modal de historial ampliado: parte de `filtered` (búsqueda + filtros
  // globales), aplica los filtros rápidos del modal y ordena por la columna elegida.
  const modalHistoryRows = useMemo(() => {
    let rows = filtered;
    const hasPlate   = a => !!(a.plate || a.vehicle?.plate);
    const hasSuspect = a => !!(a.suspect || a.suspectDesc);
    const hasFinding = a => normalizeResult(a.closeResult || a.reasonForResolution) === "with_findings" || String(a.situation || "") === "6";
    if (histFilters.relevant) rows = rows.filter(a => hasFinding(a) || hasPlate(a) || hasSuspect(a));
    if (histFilters.findings) rows = rows.filter(hasFinding);
    if (histFilters.plate)    rows = rows.filter(hasPlate);
    if (histFilters.suspect)  rows = rows.filter(hasSuspect);
    if (histFilters.unreviewed) rows = rows.filter(a => getReviewState(a) === "pending");
    if (histFilters.validated)  rows = rows.filter(a => getReviewState(a) === "validated");
    if (histSort.key) {
      const dir = histSort.dir === "asc" ? 1 : -1;
      rows = [...rows].sort((x, y) => {
        const vx = historySortValue(x, histSort.key);
        const vy = historySortValue(y, histSort.key);
        if (vx < vy) return -dir;
        if (vx > vy) return dir;
        return 0;
      });
    }
    return rows;
  }, [filtered, histFilters, histSort]);

  const durationDistribution = useMemo(() => {
    const numBins = 40; // 40 bins of 30 seconds each = 1200s
    const bins = [];
    for (let i = 0; i < numBins; i++) {
      const start = i * 30;
      const end = (i + 1) * 30;
      bins.push({
        name: `${start}s`,
        rangeStart: start,
        rangeEnd: end,
        count: 0
      });
    }
    // Overflow bin
    bins.push({
      name: ">1200s",
      rangeStart: 1200,
      rangeEnd: Infinity,
      count: 0
    });

    baseFiltered.forEach((a) => {
      let durSecs = 0;
      if (a.duration != null) {
        durSecs = a.duration;
      } else if (a.resolvedAt?.seconds && a.createdAt?.seconds) {
        durSecs = a.resolvedAt.seconds - a.createdAt.seconds;
      }
      durSecs = Math.max(0, durSecs);

      const binIndex = Math.min(numBins, Math.floor(durSecs / 30));
      bins[binIndex].count++;
    });

    return bins;
  }, [baseFiltered]);



  const renderResultsDistribution = () => {
    if (Object.keys(byResult).length === 0) return null;
    return (
      <div className="no-print" style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 9,
        padding: "12px 16px",
        marginBottom: isDesktop ? 0 : 16
      }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--subtle)", marginBottom: 8 }}>Distribución de resultados</div>
        <div style={{ display: "flex", gap: 3, height: 20, borderRadius: 5, overflow: "hidden", marginBottom: 8 }}>
          {Object.entries(byResult).map(([k, c]) => (
            <div key={k} style={{ flex: c, background: R_COLORS[k] || "#64748b", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "#fff" }}>{c}</div>
          ))}
        </div>
        <div style={{ display: "flex", gap: "6px 12px", flexWrap: "wrap", justifyContent: "center" }}>
          {Object.entries(R_LABELS).map(([k, l]) => (
            <span key={k} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 9, color: "var(--subtle)" }}>
              <span style={{ width: 7, height: 7, borderRadius: 2, background: R_COLORS[k] }} />
              {l}: <b>{byResult[k] || 0}</b>
            </span>
          ))}
        </div>
      </div>
    );
  };

  // Barra de búsqueda del historial (compartida entre el bloque inline y el modal)
  const renderHistorySearch = () => (
    <div style={{ position: "relative", display: "flex", alignItems: "center", width: "100%", marginBottom: 14 }}>
      <span style={{
        position: "absolute",
        left: 10,
        pointerEvents: "none",
        fontSize: 10,
        color: "var(--muted)",
        display: "flex",
        alignItems: "center"
      }}>🔍</span>
      <input
        className="history-search-input"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Buscar en eventos (zona, guardia, patente...)"
        style={{
          padding: "7px 30px 7px 30px",
          background: "var(--bg)",
          color: "var(--bright)",
          border: "1px solid var(--border)",
          borderRadius: 7,
          outline: "none",
          width: "100%",
          boxSizing: "border-box"
        }}
      />
      {search && (
        <button
          type="button"
          onClick={() => setSearch("")}
          style={{
            position: "absolute",
            right: 8,
            background: "none",
            border: "none",
            color: "var(--muted)",
            cursor: "pointer",
            fontSize: 10,
            padding: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          ✕
        </button>
      )}
    </div>
  );

  // Tabla del historial (compartida entre el bloque inline y el modal). Todas las
  // filas abren el detalle del evento; el Sheet de EventDetail (zIndex 10001) queda
  // por encima del modal (9999), así que el modal permanece abierto debajo.
  const renderHistoryTable = (listToRender, { minWidth = 1000, showComment = false, sort = null, onSort = null, showReview = false } = {}) => {
    const columns = [
      ...(showReview ? [{ key: "review", label: "Rev.", center: true }] : []),
      { key: "date",     label: "Fecha/Hora" },
      { key: "result",   label: "Resultado" },
      { key: "zoneName", label: "Zona" },
      { key: "duration", label: "Dur." },
      { key: "cctv",     label: "CCTV" },
      { key: "guard",    label: "Guardia" },
      { key: "signal",   label: "Señal", center: true },
      { key: "plate",    label: "Patente" },
      { key: "suspect",  label: "Sospechoso" },
      ...(showComment ? [{ key: "comment", label: "Comentario" }] : []),
    ];
    // Etiquetas compactas para la columna Resultado (sin el prefijo "Gestión ")
    const RESULT_SHORT = {
      with_findings: "Hallazgo",
      without_findings: "Sin hallazgo",
      unmanaged: "Sin gestión",
      drill: "Simulacro",
    };
    const reviewCol = showReview ? "30px " : "";
    const gridCols = showComment
      ? `${reviewCol}0.8fr 0.7fr 1.3fr 0.6fr 1fr 1fr 0.7fr 0.9fr 1.2fr 1.9fr`
      : `${reviewCol}0.8fr 0.7fr 1.5fr 0.7fr 1.1fr 1.1fr 0.8fr 1fr 1.5fr`;
    return (
    <div style={{ overflowX: "auto" }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: gridCols,
        gap: 4, padding: "0 0 6px 0",
        borderBottom: "2px solid var(--border)",
        alignItems: "center",
        minWidth
      }}>
        {columns.map(col => {
          const active = sort && sort.key === col.key;
          return (
            <div
              key={col.key}
              onClick={onSort ? () => onSort(col.key) : undefined}
              title={onSort ? `Ordenar por ${col.label}` : undefined}
              style={{
                ...TH,
                ...(active ? { color: "var(--bright)" } : {}),
                display: "flex", alignItems: "center", gap: 2,
                justifyContent: col.center ? "center" : "flex-start",
                cursor: onSort ? "pointer" : "default",
                userSelect: "none"
              }}
            >
              {col.label}
              {onSort && (
                <span style={{ fontSize: 8, opacity: active ? 1 : 0.25 }}>
                  {active ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {listToRender.length === 0 ? (
        <div style={{ padding: "24px 0", textAlign: "center", color: "var(--muted)", fontSize: 11 }}>
          No se encontraron eventos coincidentes.
        </div>
      ) : listToRender.map(a => {
        const startMs = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : null;
        const isClosed = !!a.resolvedAt;
        const situation = String(a.situation || "");
        const res = normalizeResult(a.closeResult || a.reasonForResolution);
        const isRelevant = res === "with_findings" || situation === "6" || a.plate || a.vehicle?.plate;
        return (
          <div
            key={a.docID || startMs}
            role="button"
            className={isRelevant ? "relevant-event" : "common-event"}
            onClick={() => setDetail(a)}
            style={{
              display: "grid",
              gridTemplateColumns: gridCols,
              gap: 4, padding: "4px 0",
              fontSize: 9, alignItems: "center",
              width: "100%",
              borderBottom: "1px solid var(--border)",
              color: "var(--text)", cursor: "pointer",
              textAlign: "left",
              minWidth
            }}
          >
            {showReview && (() => {
              const st = getReviewState(a); // null si está abierto
              if (!st) return <span />;
              if (st === "pending") {
                return (
                  <span title="Sin revisar" style={{ justifySelf: "center", width: 13, height: 13, borderRadius: "50%", border: "1.5px dashed var(--dim)", display: "inline-block" }} />
                );
              }
              const c = REVIEW_COLORS[st];
              const who = st === "validated" ? a.validatedBy : a.reviewedBy;
              const label = st === "validated" ? "Validado" : st === "edited" ? "Corregido" : "Confirmado";
              return (
                <span
                  title={`${label}${who ? ` por ${who}` : ""}`}
                  style={{
                    justifySelf: "center", width: 14, height: 14, borderRadius: "50%",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontSize: 8.5, fontWeight: 800,
                    background: `${c}22`, color: c, border: `1px solid ${c}55`,
                  }}
                >{st === "edited" ? "✎" : "✓"}</span>
              );
            })()}
            <span style={{ color: "var(--muted)", fontFamily: "var(--mono)", overflow: "visible", whiteSpace: "normal", lineHeight: 1.3, wordBreak: "break-word" }}>
              {startMs ? new Date(startMs).toLocaleString("es-CL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}
            </span>
            {(() => {
              const color = isClosed ? getResultColor(a) : "#dc2626";
              const full  = isClosed ? getResultLabel(a) : "Abierto";
              const short = isClosed ? (RESULT_SHORT[res] || full) : "Abierto";
              return (
                <span title={full} style={{
                  display: "inline-block",
                  justifySelf: "start",
                  maxWidth: "100%",
                  padding: "2px 7px",
                  borderRadius: 5,
                  background: `${color}1f`,
                  border: `1px solid ${color}3d`,
                  color,
                  fontSize: 9,
                  fontWeight: 700,
                  lineHeight: 1.25,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  boxSizing: "border-box"
                }}>{short}</span>
              );
            })()}
            <span style={{ fontWeight: 600, overflow: "visible", whiteSpace: "normal", lineHeight: 1.3, wordBreak: "break-word" }}>{a.zoneName}</span>
            <span style={{ color: "var(--subtle)", overflow: "visible", whiteSpace: "normal", lineHeight: 1.3 }}>{a.duration != null ? fmtDur(a.duration) : "—"}</span>
            <span style={{ color: "var(--muted)", overflow: "visible", whiteSpace: "normal", lineHeight: 1.3, wordBreak: "break-word" }}>{a.dispatchedBy || a.guardName || "—"}</span>
            <span style={{ color: "var(--muted)", overflow: "visible", whiteSpace: "normal", lineHeight: 1.3, wordBreak: "break-word" }}>
              {(Array.isArray(a.guards) ? a.guards[0] : null) || "—"}
            </span>
            {(() => {
              const signalVal = a.signalBarsNum ?? (a.signalBars && a.signalBars !== "not_specified" ? Number(a.signalBars) : null);
              if (signalVal == null) return <span style={{ color: "var(--muted)", textAlign: "center", fontFamily: "var(--mono)", fontSize: 9 }}>—</span>;
              return (
                <span style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 1.5, height: 14 }}>
                  {[1, 2, 3, 4, 5].map(i => {
                    const isActive = i <= signalVal;
                    const barColor = signalVal >= 4 ? "#22c55e" : signalVal >= 3 ? "#f59e0b" : "#ef4444";
                    return (
                      <div
                        key={i}
                        style={{
                          width: 3,
                          height: 2 + (i * 2.2),
                          borderRadius: 1,
                          background: isActive ? barColor : "var(--border)",
                          transition: "background 0.2s"
                        }}
                      />
                    );
                  })}
                </span>
              );
            })()}
            <span style={{ color: "var(--muted)", fontFamily: "var(--mono)", overflow: "visible", whiteSpace: "normal", lineHeight: 1.3, wordBreak: "break-word" }}>
              {a.vehicle?.plate || a.plate || "—"}
            </span>
            <span style={{ color: "var(--text)", overflow: "visible", whiteSpace: "normal", lineHeight: 1.3, wordBreak: "break-word" }}>
              {a.suspectDesc || (a.suspect ? "Sí" : "—")}
            </span>
            {showComment && (
              <span
                title={a.comments || a.notes || ""}
                style={{ color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: 1.3 }}
              >
                {a.comments || a.notes || "—"}
              </span>
            )}
          </div>
        );
      })}
    </div>
    );
  };

  const renderEventHistory = ({ fill = false } = {}) => {
    return (
      <div className="no-print" style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 9,
        padding: 16,
        marginBottom: isDesktop ? 0 : 24,
        ...(fill ? { height: "100%", display: "flex", flexDirection: "column", minHeight: 0 } : {})
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--subtle)" }}>Historial de Eventos</div>
            <div style={{ fontSize: 9, color: "var(--muted)", fontFamily: "var(--mono)", marginTop: 3 }}>
              Mostrando {Math.min(10, filtered.length)} de {filtered.length}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                type="button"
                className="panel-link-btn"
                onClick={() => setActiveModal("history")}
                title="Ver historial completo en pantalla ampliada"
                style={{
                  background: "transparent",
                  color: "var(--muted)",
                  fontSize: 9,
                  fontWeight: 700,
                  cursor: "pointer"
                }}
                onMouseEnter={e => e.currentTarget.style.color = "var(--bright)"}
                onMouseLeave={e => e.currentTarget.style.color = "var(--muted)"}
              >
                Ver todo ↗
              </button>
              {isSupervisor && (
                <button
                  type="button"
                  className="panel-link-btn"
                  onClick={handleExportCSV}
                  style={{
                    background: "transparent",
                    color: "#22c55e",
                    fontSize: 9,
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 3
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = "#4ade80"}
                  onMouseLeave={e => e.currentTarget.style.color = "#22c55e"}
                >
                  📥 Excel
                </button>
              )}
            </div>
            {/* Buscar → abre el modal "Ver todo", donde vive el buscador */}
            <button
              type="button"
              onClick={() => setActiveModal("history")}
              title="Buscar en el historial completo"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "var(--bg)",
                border: "1px solid var(--border)",
                borderRadius: 7,
                color: "var(--muted)",
                fontSize: 9,
                fontWeight: 600,
                padding: "5px 10px",
                cursor: "pointer",
                transition: "border-color 0.15s ease, color 0.15s ease"
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--muted)"; e.currentTarget.style.color = "var(--subtle)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--muted)"; }}
            >
              🔍 Buscar eventos...
            </button>
          </div>
        </div>

        {/* Historial Table */}
        <div style={fill ? { flex: 1, overflowY: "auto", minHeight: 0 } : undefined}>
          {renderHistoryTable(filtered.slice(0, 10), { showReview: reviewRoles.canReview })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: "40px 0", display: "flex", justifyContent: "center", minHeight: 400, alignItems: "center" }}>
        <Loading size="14" />
      </div>
    );
  }

  return (
    <div className="supervisor-container">
      {/* ── Screen Layout (Clean & Uncluttered) ── */}
      {/* Screen Header */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        marginBottom: 16,
        gap: 10,
        width: "100%",
        borderBottom: "1px solid var(--border)",
        paddingBottom: 12
      }} className="no-print">
        {/* Top row: Title + Buttons (and Period Selector on Desktop) */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap", width: "100%" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "var(--bright)", display: "flex", alignItems: "center", gap: 6 }}>
              <span>Panel Supervisor</span>
            </div>
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
              Eventos de {periodLabel} · {filtered.length} total
              {facility?.visibleHoursRange && (
                <span style={{
                  fontSize: 9,
                  fontWeight: 600,
                  background: "rgba(34, 197, 94, 0.08)",
                  border: "1px solid rgba(34, 197, 94, 0.2)",
                  color: "#22c55e",
                  padding: "1px 6px",
                  borderRadius: 10,
                  marginLeft: 8,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3.5
                }}>
                  Turno: {String(facility.visibleHoursRange[0]).padStart(2, "0")}:00 - {String(facility.visibleHoursRange[1]).padStart(2, "0")}:00
                </span>
              )}
            </div>
          </div>

          {/* Period selector & Resultado - Center (only on desktop) */}
          {isDesktop && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{
                display: "flex",
                gap: 2,
                background: "var(--bg)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: 2
              }}>
                {[["day", "Hoy"], ["week", "Semana"], ["month", "Mes"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setPeriod(k)} style={{
                    padding: "4px 10px", borderRadius: 4, border: "none",
                    background: period === k ? "var(--surface)" : "transparent",
                    color: period === k ? "var(--bright)" : "var(--muted)",
                    fontSize: 10, fontWeight: 600, cursor: "pointer",
                    transition: "background 0.15s, color 0.15s"
                  }}>{l}</button>
                ))}
              </div>

              {/* Resultado Dropdown */}
              <div style={{
                display: "flex",
                background: "var(--bg)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: 2
              }}>
                <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                  <select
                    className="header-select"
                    value={filterResult}
                    onChange={e => setFilterResult(e.target.value)}
                    style={{
                      padding: "4px 24px 4px 10px",
                      fontSize: 10,
                      background: "var(--surface)",
                      color: "var(--bright)",
                      border: "none",
                      borderRadius: 4,
                      cursor: "pointer",
                      outline: "none",
                      appearance: "none",
                      WebkitAppearance: "none",
                      MozAppearance: "none",
                      height: 22,
                      fontWeight: 600
                    }}
                  >
                    <option value="all" style={{ fontSize: 9 }}>Todos los resultados</option>
                    <option value="with_findings" style={{ fontSize: 9 }}>Con hallazgo</option>
                    <option value="without_findings" style={{ fontSize: 9 }}>Sin hallazgo</option>
                    <option value="drill" style={{ fontSize: 9 }}>Simulacro / Prueba</option>
                    <option value="unmanaged" style={{ fontSize: 9 }}>Sin gestión / Cierre auto</option>
                  </select>
                  <span style={{
                    position: "absolute",
                    right: 8,
                    pointerEvents: "none",
                    fontSize: 8,
                    color: "var(--muted)"
                  }}>▼</span>
                </div>
              </div>

              {/* Recintos Dropdown */}
              <div style={{
                display: "flex",
                background: "var(--bg)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: 2,
                position: "relative"
              }}>
                <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                  <button
                    type="button"
                    onClick={() => setFacilityDropdownOpen(!facilityDropdownOpen)}
                    style={{
                      padding: "4px 24px 4px 10px",
                      fontSize: 10,
                      background: "var(--surface)",
                      color: "var(--bright)",
                      border: "none",
                      borderRadius: 4,
                      cursor: "pointer",
                      outline: "none",
                      height: 22,
                      fontWeight: 600,
                      textAlign: "left",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 150,
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    {(() => {
                      if (isCopec) {
                        if (selectedZones.length === allZones.length && allZones.length > 0) {
                          return "Recintos: Todos";
                        }
                        if (selectedZones.length === 1) {
                          return cleanCopecName(selectedZones[0].zoneName);
                        }
                        return `Recintos: ${selectedZones.length} sel.`;
                      } else {
                        if (selectedFacilityIds.length === facilitiesList.length && facilitiesList.length > 0) {
                          return "Recintos: Todos";
                        }
                        if (selectedFacilityIds.length === 1) {
                          const facName = facilitiesList.find(f => f.id === selectedFacilityIds[0])?.title || "Recinto";
                          return cleanCopecName(facName);
                        }
                        return `Recintos: ${selectedFacilityIds.length} sel.`;
                      }
                    })()}
                  </button>
                  <span style={{
                    position: "absolute",
                    right: 8,
                    pointerEvents: "none",
                    fontSize: 8,
                    color: "var(--muted)"
                  }}>▼</span>
                </div>

                {facilityDropdownOpen && (
                  <>
                    <div 
                      onClick={() => setFacilityDropdownOpen(false)} 
                      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} 
                    />
                    <div style={{
                      position: "absolute",
                      right: 0,
                      top: 30,
                      background: "var(--card)",
                      border: "1px solid var(--border)",
                      borderRadius: 6,
                      padding: "8px 10px",
                      zIndex: 1000,
                      minWidth: 180,
                      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.5)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 6
                    }}>
                      {isCopec ? (
                        <>
                          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9.5, cursor: "pointer", fontWeight: 700, color: "var(--bright)", borderBottom: "1px solid var(--border)", paddingBottom: 4, marginBottom: 2 }}>
                            <input
                              type="checkbox"
                              checked={selectedZones.length === allZones.length && allZones.length > 0}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedZones(allZones);
                                } else {
                                  setSelectedZones([]);
                                }
                              }}
                              style={{ cursor: "pointer" }}
                            />
                            Todos los recintos
                          </label>
                          {allZones.map(z => {
                            return (
                              <label key={z.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9.5, cursor: "pointer", color: "var(--text)", fontWeight: 500 }}>
                                <input
                                  type="checkbox"
                                  checked={selectedZones.some(sz => String(sz.id) === String(z.id))}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedZones([...selectedZones, z]);
                                    } else {
                                      setSelectedZones(selectedZones.filter(sz => String(sz.id) !== String(z.id)));
                                    }
                                  }}
                                  style={{ cursor: "pointer" }}
                                />
                                {cleanCopecName(z.zoneName)}
                              </label>
                            );
                          })}
                        </>
                      ) : (
                        <>
                          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9.5, cursor: "pointer", fontWeight: 700, color: "var(--bright)", borderBottom: "1px solid var(--border)", paddingBottom: 4, marginBottom: 2 }}>
                            <input
                              type="checkbox"
                              checked={selectedFacilityIds.length === facilitiesList.length && facilitiesList.length > 0}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedFacilityIds(facilitiesList.map(f => f.id));
                                } else {
                                  setSelectedFacilityIds([]);
                                }
                              }}
                              style={{ cursor: "pointer" }}
                            />
                            Todos los recintos
                          </label>
                          {facilitiesList.map(f => {
                            return (
                              <label key={f.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9.5, cursor: "pointer", color: "var(--text)", fontWeight: 500 }}>
                                <input
                                  type="checkbox"
                                  checked={selectedFacilityIds.includes(f.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedFacilityIds([...selectedFacilityIds, f.id]);
                                    } else {
                                      setSelectedFacilityIds(selectedFacilityIds.filter(id => id !== f.id));
                                    }
                                  }}
                                  style={{ cursor: "pointer" }}
                                />
                                {cleanCopecName(f.title || f.company)}
                              </label>
                            );
                          })}
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Botón Duración (Desktop) */}
              <div style={{
                display: "flex",
                background: "var(--bg)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: 2,
                position: "relative"
              }}>
                <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                  <button
                    type="button"
                    onClick={() => setActiveModal("duration")}
                    style={{
                      padding: "4px 24px 4px 10px",
                      fontSize: 10,
                      background: "var(--surface)",
                      color: (minDurationFilter > 0 || maxDurationFilter < 1200) ? "#60a5fa" : "var(--bright)",
                      border: "none",
                      borderRadius: 4,
                      cursor: "pointer",
                      outline: "none",
                      height: 22,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 4
                    }}
                  >
                    <span>
                      {(() => {
                        const min = minDurationFilter;
                        const max = maxDurationFilter;
                        if (min === 0 && max === 1200) return "Duración: Todos";
                        const formatVal = (s) => {
                          if (s < 60) return `${s}s`;
                          const m = Math.floor(s / 60);
                          const r = s % 60;
                          return r === 0 ? `${m}m` : `${m}m ${r}s`;
                        };
                        if (min > 0 && max === 1200) return `Duración: >${formatVal(min)}`;
                        if (min === 0 && max < 1200) return `Duración: <=${formatVal(max)}`;
                        return `Duración: ${formatVal(min)} a ${formatVal(max)}`;
                      })()}
                    </span>
                  </button>
                  <span style={{
                    position: "absolute",
                    right: 8,
                    pointerEvents: "none",
                    fontSize: 8,
                    color: "var(--muted)"
                  }}>▼</span>
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }} className="no-print">
            {/* Export Report Dropdown */}
            {isSupervisor && (
              <div style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={() => setShowExportDropdown(!showExportDropdown)}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 6,
                    border: "1px solid rgba(168, 85, 247, 0.4)",
                    background: "rgba(168, 85, 247, 0.15)",
                    color: "#c084fc",
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4
                  }}
                >
                  📥 Exportar Reporte
                </button>
                {showExportDropdown && (
                  <div style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    marginTop: 6,
                    background: "var(--card)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    padding: 6,
                    zIndex: 1000,
                    boxShadow: "0 10px 15px -3px rgba(0,0,0,0.5)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    minWidth: 190
                  }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowExportDropdown(false);
                        handleExportPDF();
                      }}
                      style={{
                        padding: "6px 10px",
                        borderRadius: 4,
                        border: "none",
                        background: "transparent",
                        color: "var(--bright)",
                        fontSize: 10,
                        fontWeight: 600,
                        textAlign: "left",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 6
                      }}
                      onMouseEnter={e => e.target.style.background = "var(--surface)"}
                      onMouseLeave={e => e.target.style.background = "transparent"}
                    >
                      📄 PDF Presentación (Slides)
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowExportDropdown(false);
                        handlePrintPaper();
                      }}
                      style={{
                        padding: "6px 10px",
                        borderRadius: 4,
                        border: "none",
                        background: "transparent",
                        color: "var(--bright)",
                        fontSize: 10,
                        fontWeight: 600,
                        textAlign: "left",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 6
                      }}
                      onMouseEnter={e => e.target.style.background = "var(--surface)"}
                      onMouseLeave={e => e.target.style.background = "transparent"}
                    >
                      🖨️ PDF Impresión (Papel)
                    </button>
                  </div>
                )}
              </div>
            )}

            {reviewRoles.canReview && (
              <button
                type="button"
                onClick={() => setShowInbox(true)}
                title="Bandeja de revisión de eventos"
                style={{
                  padding: "5px 10px", borderRadius: 6,
                  border: `1px solid ${pendingReviewCount > 0 ? "#f59e0b55" : "var(--border)"}`,
                  background: pendingReviewCount > 0 ? "#f59e0b18" : "var(--surface)",
                  color: pendingReviewCount > 0 ? "#fbbf24" : "var(--bright)",
                  fontSize: 10, fontWeight: 600, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 5,
                }}
              >
                📋 Revisión
                {pendingReviewCount > 0 && (
                  <span style={{ background: "#f59e0b", color: "#1a1205", borderRadius: 999, fontSize: 8.5, padding: "1px 6px", fontWeight: 800 }}>
                    {pendingReviewCount}
                  </span>
                )}
              </button>
            )}
            {(isSupervisor || isDevUser) && (
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                style={{
                  padding: "5px 10px",
                  borderRadius: 6,
                  border: "1px solid var(--border)",
                  background: "var(--surface)",
                  color: "var(--bright)",
                  fontSize: 10,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 4
                }}
              >
                ⚙️ Ajustes
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Period selection toggle & Resultado - centered below header (only on mobile) */}
      {!isDesktop && (
        <div className="no-print" style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginBottom: 14, marginTop: -4, flexWrap: "wrap" }}>
          <div style={{
            display: "flex",
            gap: 2,
            background: "var(--bg)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            padding: 2
          }}>
            {[["day", "Hoy"], ["week", "Semana"], ["month", "Mes"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setPeriod(k)} style={{
                padding: "4px 10px", borderRadius: 4, border: "none",
                background: period === k ? "var(--surface)" : "transparent",
                color: period === k ? "var(--bright)" : "var(--muted)",
                fontSize: 10, fontWeight: 600, cursor: "pointer",
                transition: "background 0.15s, color 0.15s"
              }}>{l}</button>
            ))}
          </div>

          {/* Resultado Dropdown */}
          <div style={{
            display: "flex",
            background: "var(--bg)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            padding: 2
          }}>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <select
                className="header-select"
                value={filterResult}
                onChange={e => setFilterResult(e.target.value)}
                style={{
                  padding: "4px 24px 4px 10px",
                  fontSize: 10,
                  background: "var(--surface)",
                  color: "var(--bright)",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  outline: "none",
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  height: 22,
                  fontWeight: 600
                }}
              >
                <option value="all" style={{ fontSize: 9 }}>Todos los resultados</option>
                <option value="with_findings" style={{ fontSize: 9 }}>Con hallazgo</option>
                <option value="without_findings" style={{ fontSize: 9 }}>Sin hallazgo</option>
                <option value="drill" style={{ fontSize: 9 }}>Simulacro / Prueba</option>
                <option value="unmanaged" style={{ fontSize: 9 }}>Sin gestión / Cierre auto</option>
              </select>
              <span style={{
                position: "absolute",
                right: 8,
                pointerEvents: "none",
                fontSize: 8,
                color: "var(--muted)"
              }}>▼</span>
            </div>
          </div>

          {/* Recintos Dropdown */}
          <div style={{
            display: "flex",
            background: "var(--bg)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            padding: 2,
            position: "relative"
          }}>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <button
                type="button"
                onClick={() => setFacilityDropdownOpen(!facilityDropdownOpen)}
                style={{
                  padding: "4px 24px 4px 10px",
                  fontSize: 10,
                  background: "var(--surface)",
                  color: "var(--bright)",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  outline: "none",
                  height: 22,
                  fontWeight: 600,
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 150,
                  display: "flex",
                  alignItems: "center"
                }}
              >
                {(() => {
                  if (isCopec) {
                    if (selectedZones.length === allZones.length && allZones.length > 0) {
                      return "Recintos: Todos";
                    }
                    if (selectedZones.length === 1) {
                      return cleanCopecName(selectedZones[0].zoneName);
                    }
                    return `Recintos: ${selectedZones.length} sel.`;
                  } else {
                    if (selectedFacilityIds.length === facilitiesList.length && facilitiesList.length > 0) {
                      return "Recintos: Todos";
                    }
                    if (selectedFacilityIds.length === 1) {
                      const facName = facilitiesList.find(f => f.id === selectedFacilityIds[0])?.title || "Recinto";
                      return cleanCopecName(facName);
                    }
                    return `Recintos: ${selectedFacilityIds.length} sel.`;
                  }
                })()}
              </button>
              <span style={{
                position: "absolute",
                right: 8,
                pointerEvents: "none",
                fontSize: 8,
                color: "var(--muted)"
              }}>▼</span>
            </div>

            {facilityDropdownOpen && (
              <>
                <div 
                  onClick={() => setFacilityDropdownOpen(false)} 
                  style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} 
                />
                <div style={{
                  position: "absolute",
                  right: 0,
                  top: 30,
                  background: "var(--card)",
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  padding: "8px 10px",
                  zIndex: 1000,
                  minWidth: 180,
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.5)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 6
                }}>
                  {isCopec ? (
                    <>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9.5, cursor: "pointer", fontWeight: 700, color: "var(--bright)", borderBottom: "1px solid var(--border)", paddingBottom: 4, marginBottom: 2 }}>
                        <input
                          type="checkbox"
                          checked={selectedZones.length === allZones.length && allZones.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedZones(allZones);
                            } else {
                              setSelectedZones([]);
                            }
                          }}
                          style={{ cursor: "pointer" }}
                        />
                        Todos los recintos
                      </label>
                      {allZones.map(z => {
                        return (
                          <label key={z.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9.5, cursor: "pointer", color: "var(--text)", fontWeight: 500 }}>
                            <input
                              type="checkbox"
                              checked={selectedZones.some(sz => String(sz.id) === String(z.id))}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedZones([...selectedZones, z]);
                                } else {
                                  setSelectedZones(selectedZones.filter(sz => String(sz.id) !== String(z.id)));
                                }
                              }}
                              style={{ cursor: "pointer" }}
                            />
                            {cleanCopecName(z.zoneName)}
                          </label>
                        );
                      })}
                    </>
                  ) : (
                    <>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9.5, cursor: "pointer", fontWeight: 700, color: "var(--bright)", borderBottom: "1px solid var(--border)", paddingBottom: 4, marginBottom: 2 }}>
                        <input
                          type="checkbox"
                          checked={selectedFacilityIds.length === facilitiesList.length && facilitiesList.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedFacilityIds(facilitiesList.map(f => f.id));
                            } else {
                              setSelectedFacilityIds([]);
                            }
                          }}
                          style={{ cursor: "pointer" }}
                        />
                        Todos los recintos
                      </label>
                      {facilitiesList.map(f => {
                        return (
                          <label key={f.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9.5, cursor: "pointer", color: "var(--text)", fontWeight: 500 }}>
                            <input
                              type="checkbox"
                              checked={selectedFacilityIds.includes(f.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedFacilityIds([...selectedFacilityIds, f.id]);
                                } else {
                                  setSelectedFacilityIds(selectedFacilityIds.filter(id => id !== f.id));
                                }
                              }}
                              style={{ cursor: "pointer" }}
                            />
                            {cleanCopecName(f.title || f.company)}
                          </label>
                        );
                      })}
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Botón Duración (Mobile) */}
          <div style={{
            display: "flex",
            background: "var(--bg)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            padding: 2,
            position: "relative"
          }}>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <button
                type="button"
                onClick={() => setActiveModal("duration")}
                style={{
                  padding: "4px 24px 4px 10px",
                  fontSize: 10,
                  background: "var(--surface)",
                  color: (minDurationFilter > 0 || maxDurationFilter < 1200) ? "#60a5fa" : "var(--bright)",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  outline: "none",
                  height: 22,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 4
                }}
              >
                <span>
                  {(() => {
                    const min = minDurationFilter;
                    const max = maxDurationFilter;
                    if (min === 0 && max === 1200) return "Duración: Todos";
                    const formatVal = (s) => {
                      if (s < 60) return `${s}s`;
                      const m = Math.floor(s / 60);
                      const r = s % 60;
                      return r === 0 ? `${m}m` : `${m}m ${r}s`;
                    };
                    if (min > 0 && max === 1200) return `Duración: >${formatVal(min)}`;
                    if (min === 0 && max < 1200) return `Duración: <=${formatVal(max)}`;
                    return `Duración: ${formatVal(min)} a ${formatVal(max)}`;
                  })()}
                </span>
              </button>
              <span style={{
                position: "absolute",
                right: 8,
                pointerEvents: "none",
                fontSize: 8,
                color: "var(--muted)"
              }}>▼</span>
            </div>
          </div>
        </div>
      )}


      {/* Clickable KPI Grid / Desktop Live Panel */}
      {(() => {
        const managedPct = totalNoDrill > 0 ? Math.round(managedByHuman / totalNoDrill * 100) : 0;
        const findingsPct = closed.length > 0 ? Math.round(withFindings / closed.length * 100) : 0;

        const cmpManaged = (() => {
          const diff = managedPct - prevStats.managedPct;
          if (diff > 0) return { text: `▲ +${diff}%`, color: "#22c55e" };
          if (diff < 0) return { text: `▼ ${diff}%`, color: "#ef4444" };
          return { text: "= vs ant.", color: "var(--muted)" };
        })();

        const cmpDisp = (() => {
          if (!avgDisp || !prevStats.avgDisp) return null;
          const pct = Math.round((avgDisp - prevStats.avgDisp) / prevStats.avgDisp * 100);
          if (pct < 0) return { text: `▼ ${pct}%`, color: "#22c55e" };
          if (pct > 0) return { text: `▲ +${pct}%`, color: "#ef4444" };
          return { text: "= vs ant.", color: "var(--muted)" };
        })();

        const cmpArr = (() => {
          if (!avgArr || !prevStats.avgArr) return null;
          const pct = Math.round((avgArr - prevStats.avgArr) / prevStats.avgArr * 100);
          if (pct < 0) return { text: `▼ ${pct}%`, color: "#22c55e" };
          if (pct > 0) return { text: `▲ +${pct}%`, color: "#ef4444" };
          return { text: "= vs ant.", color: "var(--muted)" };
        })();

        const cmpFindings = (() => {
          const diff = findingsPct - prevStats.findingsPct;
          if (diff > 0) return { text: `▲ +${diff}%`, color: "#22c55e" };
          if (diff < 0) return { text: `▼ ${diff}%`, color: "#ef4444" };
          return { text: "= vs ant.", color: "var(--muted)" };
        })();

        const cmpActivity = (() => {
          if (!prevStats.minutesTotal) return null;
          const pct = Math.round((currentMinutes - prevStats.minutesTotal) / prevStats.minutesTotal * 100);
          if (pct < 0) return { text: `▼ ${pct}%`, color: "#22c55e" };
          if (pct > 0) return { text: `▲ +${pct}%`, color: "#ef4444" };
          return { text: "= vs ant.", color: "var(--muted)" };
        })();

        if (isDesktop) {
          return (
            <div style={{ display: "flex", gap: 16, alignItems: "stretch", width: "100%" }} className="no-print">
              {/* Left Column: Dashboard Panels Grid */}
              <div style={{ flex: "1 1 0%", minWidth: 0 }}>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "1.3fr 1.3fr 0.85fr 0.85fr 0.85fr 0.85fr",
                  gap: 12
                }}>
              {/* PANEL A: GESTIÓN, HALLAZGOS Y ACTIVIDAD */}
              <div style={{
                gridColumn: "span 2",
                position: "relative",
                background: "var(--card)",
                border: "1px solid var(--border)",
                borderRadius: 12,
                padding: "12px 14px",
                height: 300,
                display: "flex",
                flexDirection: "column"
              }}>
                {/* Floating KPIs */}
                <div style={{
                  position: "absolute",
                  top: 10,
                  left: 14,
                  right: 14,
                  zIndex: 10,
                  display: "flex",
                  justifyContent: "space-between",
                  pointerEvents: "none"
                }}>
                  {/* Tasa de Gestión */}
                  <div>
                    <div style={{ fontSize: 7.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".04em" }}>Tasa de gestión</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 3, marginTop: 1 }}>
                      <span style={{ fontSize: 15, fontWeight: 800, color: managedPct >= 80 ? "#22c55e" : managedPct >= 50 ? "#f59e0b" : "#ef4444", fontFamily: "var(--mono)" }}>{managedPct}%</span>
                      <span style={{ fontSize: 7.5, fontWeight: 800, color: cmpManaged.color }}>{cmpManaged.text}</span>
                    </div>
                    <div style={{ fontSize: 7.5, color: "var(--dim)", marginTop: 0.5 }}>{managedByHuman}/{totalNoDrill} ev.</div>
                  </div>

                  {/* Gestión con Hallazgos */}
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 7.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".04em" }}>Con Hallazgos</div>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 3, marginTop: 1 }}>
                      <span style={{ fontSize: 15, fontWeight: 800, color: "#22c55e", fontFamily: "var(--mono)" }}>{findingsPct}%</span>
                      <span style={{ fontSize: 7.5, fontWeight: 800, color: cmpFindings.color }}>{cmpFindings.text}</span>
                    </div>
                    <div style={{ fontSize: 7.5, color: "var(--dim)", marginTop: 0.5 }}>{withFindings}/{closed.length} cerr.</div>
                  </div>

                  {/* Nivel Actividad */}
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 7.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".04em" }}>Nivel Actividad</div>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "flex-end", gap: 3, marginTop: 1 }}>
                      {cmpActivity && <span style={{ fontSize: 7.5, fontWeight: 800, color: cmpActivity.color }}>{cmpActivity.text}</span>}
                      <span style={{ fontSize: 13, fontWeight: 800, color: activityLevel.color }}>{activityLevel.label}</span>
                    </div>
                    <div style={{ fontSize: 7.5, color: "var(--dim)", marginTop: 0.5 }}>{currentMinutes}m tot.</div>
                  </div>
                </div>

                {/* AreaChart */}
                <div style={{ width: "100%", height: "100%", position: "relative" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={mergedChartData} margin={{ top: 52, right: 2, left: -44, bottom: 0 }}>
                      <defs>
                        <linearGradient id="deskColorTotal" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.12}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.01}/>
                        </linearGradient>
                        <linearGradient id="deskColorGest" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22c55e" stopOpacity={0.12}/>
                          <stop offset="95%" stopColor="#22c55e" stopOpacity={0.01}/>
                        </linearGradient>
                        <linearGradient id="deskColorHallazgo" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#a855f7" stopOpacity={0.12}/>
                          <stop offset="95%" stopColor="#a855f7" stopOpacity={0.01}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 7.5, fill: 'var(--muted)', fontFamily: 'var(--mono)' }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 7.5, fill: 'var(--muted)', fontFamily: 'var(--mono)' }}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "var(--card)",
                          border: "1px solid var(--border)",
                          borderRadius: 6,
                          fontSize: 9,
                          color: "var(--text)"
                        }}
                        itemStyle={{ padding: "1px 0" }}
                      />
                      <Area type="monotone" dataKey="Total" stroke="#3b82f6" strokeWidth={1.8} fillOpacity={1} fill="url(#deskColorTotal)" name="Alertas Totales">
                        <LabelList dataKey="Total" position="top" style={{ fontSize: 7.5, fill: "#3b82f6", fontWeight: 700, fontFamily: "var(--mono)" }} />
                      </Area>
                      <Area type="monotone" dataKey="Gestionados" stroke="#22c55e" strokeWidth={1.8} fillOpacity={1} fill="url(#deskColorGest)" name="Gestionadas">
                        <LabelList dataKey="Gestionados" position="top" style={{ fontSize: 7.5, fill: "#22c55e", fontWeight: 700, fontFamily: "var(--mono)" }} />
                      </Area>
                      <Area type="monotone" dataKey="ConHallazgos" stroke="#a855f7" strokeWidth={1.8} fillOpacity={1} fill="url(#deskColorHallazgo)" name="Con Hallazgos">
                        <LabelList dataKey="ConHallazgos" position="top" style={{ fontSize: 7.5, fill: "#a855f7", fontWeight: 700, fontFamily: "var(--mono)" }} />
                      </Area>
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* PANEL E1: HEATMAP DE INHIBICIÓN (Spans 4 columns) */}
              <div style={{
                gridColumn: "span 4",
                background: "var(--card)",
                border: "1px solid var(--border)",
                borderRadius: 12,
                padding: "12px 14px",
                height: 300,
                display: "flex",
                flexDirection: "column"
              }}>
                {/* Header */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                  borderBottom: "1px solid var(--border)",
                  paddingBottom: 6
                }}>
                  <div>
                    <div style={{ fontSize: 7.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase" }}>Heatmap de Inhibición</div>
                    <div style={{ fontSize: 9, fontWeight: 800, color: "var(--bright)", marginTop: 1 }}>Cruces de tramos horarios vs. días de la semana (Tiempo de Señal)</div>
                  </div>
                  {selectedHeatmapCell && (
                    <button
                      onClick={() => setSelectedHeatmapCell(null)}
                      style={{
                        background: "rgba(239, 68, 68, 0.15)",
                        color: "#ef4444",
                        border: "1px solid rgba(239, 68, 68, 0.3)",
                        borderRadius: 4,
                        padding: "2px 8px",
                        fontSize: 8,
                        fontWeight: 700,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        textTransform: "uppercase"
                      }}
                    >
                      ✕ Quitar Filtro
                    </button>
                  )}
                </div>

                {/* Heatmap Grid */}
                <div style={{ flex: 1, minHeight: 0, marginTop: 4 }}>
                  {heatmapData.maxVal > 0 ? (() => {
                    const { grid, maxVal } = heatmapData;
                    const daysHeader = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
                    return (
                      <div style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "40px repeat(7, 1fr)", gap: 2.5, textAlign: "center", marginBottom: 3 }}>
                          <div></div>
                          {daysHeader.map(d => (
                            <div key={d} style={{ fontSize: 7.5, fontWeight: 700, color: "var(--subtle)" }}>{d}</div>
                          ))}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2.5, overflowY: "auto", flex: 1 }}>
                          {grid.map((row) => (
                            <div key={row.label} style={{ display: "grid", gridTemplateColumns: "40px repeat(7, 1fr)", gap: 2.5, alignItems: "center" }}>
                              <div style={{ fontSize: 7.5, color: "var(--muted)", fontFamily: "var(--mono)", textAlign: "right", paddingRight: 3 }}>{row.label}</div>
                              {row.days.map((val, dIdx) => {
                                const isSelected = selectedHeatmapCell && selectedHeatmapCell.rowIdx === row.rowIdx && selectedHeatmapCell.dayIdx === dIdx;
                                const anySelected = !!selectedHeatmapCell;
                                const ratio = maxVal > 0 ? val / maxVal : 0;
                                const opacity = val > 0 ? 0.15 + ratio * 0.85 : 0.03;
                                
                                let bg = val > 0 ? `rgba(239, 68, 68, ${opacity})` : "rgba(255, 255, 255, 0.02)";
                                let border = val > 0 ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid var(--border)";
                                
                                if (anySelected) {
                                  if (isSelected) {
                                    border = "1.5px solid #3b82f6";
                                  } else {
                                    bg = val > 0 ? `rgba(239, 68, 68, ${opacity * 0.3})` : "rgba(255, 255, 255, 0.005)";
                                    border = val > 0 ? "1px solid rgba(239, 68, 68, 0.1)" : "1px solid rgba(255, 255, 255, 0.02)";
                                  }
                                }
                                const textColor = val > 0 && ratio > 0.55 ? "#fff" : "var(--muted)";
                                return (
                                  <div
                                    key={dIdx}
                                    title={`Día: ${daysHeader[dIdx]} · Tramo: ${row.label} · Inhibición: ${fmtDur(val)}`}
                                    onClick={() => {
                                      if (val > 0) {
                                        if (isSelected) {
                                          setSelectedHeatmapCell(null);
                                        } else {
                                          setSelectedHeatmapCell({ rowIdx: row.rowIdx, dayIdx: dIdx });
                                        }
                                      }
                                    }}
                                    style={{
                                      background: bg,
                                      border: border,
                                      borderRadius: 3,
                                      height: 18,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: 7.5,
                                      fontFamily: "var(--mono)",
                                      fontWeight: 700,
                                      color: isSelected ? "#3b82f6" : textColor,
                                      cursor: val > 0 ? "pointer" : "default",
                                      opacity: anySelected && !isSelected ? 0.4 : 1,
                                      boxShadow: isSelected ? "0 0 0 2px rgba(59, 130, 246, 0.3)" : "none",
                                      transition: "all 0.2s ease"
                                    }}
                                  >
                                    {fmtHeatVal(val)}
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })() : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, color: "var(--muted)", fontSize: 9 }}>
                      Sin registro de inhibiciones en este periodo
                    </div>
                  )}
                </div>
              </div>

              {/* PANEL E2 (swap): HISTORIAL DE EVENTOS — slot ancho, alineado con la fila (Row 2 left, span 2) */}
              <div style={{ gridColumn: "span 2", height: 300, minHeight: 0, display: "flex", flexDirection: "column" }}>
                {renderEventHistory({ fill: true })}
              </div>

              {/* PANEL C: RENDIMIENTO CCTV (Spans 2 columns) */}
              <div style={{
                gridColumn: "span 2",
                background: "var(--card)",
                border: "1px solid var(--border)",
                borderRadius: 12,
                padding: "12px 14px",
                height: 300,
                display: "flex",
                flexDirection: "column"
              }}>
                {/* Header Metrics */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: 8,
                  borderBottom: "1px solid var(--border)",
                  paddingBottom: 6
                }}>
                  <div>
                    <div style={{ fontSize: 7.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase" }}>Despacho CCTV</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginTop: 1 }}>
                      <span style={{ fontSize: 16, fontWeight: 800, color: "#f59e0b", fontFamily: "var(--mono)" }}>{avgDisp ? fmtDur(avgDisp) : "N/A"}</span>
                      {cmpDisp && <span style={{ fontSize: 7.5, fontWeight: 800, color: cmpDisp.color }}>{cmpDisp.text}</span>}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                    <button
                      type="button"
                      className="panel-link-btn"
                      onClick={() => setActiveModal("cctv")}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "var(--muted)",
                        fontSize: 9,
                        fontWeight: 700,
                        cursor: "pointer",
                        padding: "0 0 4px 0",
                        transition: "color 0.2s"
                      }}
                      onMouseEnter={e => e.target.style.color = "var(--bright)"}
                      onMouseLeave={e => e.target.style.color = "var(--muted)"}
                    >
                      Ver todo ↗
                    </button>
                    <div style={{ fontSize: 7.5, color: "var(--muted)", textTransform: "uppercase" }}>Prom. Reac: <b style={{ color: "#f59e0b", fontFamily: "var(--mono)" }}>{avgReactionTime ? fmtDur(avgReactionTime) : "—"}</b></div>
                    <div style={{ fontSize: 7.5, color: "var(--muted)", marginTop: 1, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", maxWidth: 120 }}>Líder: <b style={{ color: "#22c55e" }}>{bestOperator.split(" ")[0]}</b></div>
                  </div>
                </div>

                {/* Operator table */}
                <div style={{ flex: 1, overflowY: "auto" }}>
                  {Object.keys(opStats).length > 0 ? (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 40px 40px 40px 50px", gap: "4px 6px", fontSize: 9 }}>
                      <div style={TH}>Operador</div>
                      <div style={{ ...TH, textAlign: "center" }}>Desp.</div>
                      <div style={{ ...TH, textAlign: "center" }}>Cierr.</div>
                      <div style={{ ...TH, textAlign: "center" }}>Exit.</div>
                      <div style={{ ...TH, textAlign: "center" }}>T. Reac.</div>
                      {(() => {
                        const sorted = Object.entries(opStats).sort((a,b)=>b[1].disp - a[1].disp);
                        return sorted.map(([n, s]) => {
                          const avgReact = s.reactionTimes && s.reactionTimes.length
                            ? Math.round(s.reactionTimes.reduce((a, b) => a + b, 0) / s.reactionTimes.length)
                            : null;
                          return (
                            <React.Fragment key={n}>
                              <div style={{ color: "var(--text)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n}</div>
                              <div style={{ color: "var(--subtle)", fontFamily: "var(--mono)", textAlign: "center" }}>{s.disp}</div>
                              <div style={{ color: "var(--subtle)", fontFamily: "var(--mono)", textAlign: "center" }}>{s.close}</div>
                              <div style={{ color: s.ok>0?"#22c55e":"var(--dim)", fontFamily: "var(--mono)", textAlign: "center", fontWeight: 700 }}>{s.ok}</div>
                              <div style={{ color: avgReact&&avgReact<60?"#22c55e":avgReact&&avgReact<180?"#f59e0b":"#ef4444", fontFamily: "var(--mono)", textAlign: "center", fontWeight: 600 }}>
                                {avgReact ? fmtDur(avgReact) : "—"}
                              </div>
                            </React.Fragment>
                          );
                        });
                      })()}
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--muted)", fontSize: 9 }}>
                      Sin datos en este periodo
                    </div>
                  )}
                </div>
              </div>

              {/* PANEL D: RENDIMIENTO GUARDIA (Spans 2 columns) */}
              <div style={{
                gridColumn: "span 2",
                background: "var(--card)",
                border: "1px solid var(--border)",
                borderRadius: 12,
                padding: "12px 10px",
                height: 300,
                display: "flex",
                flexDirection: "column"
              }}>
                {/* Header Metrics */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: 8,
                  borderBottom: "1px solid var(--border)",
                  paddingBottom: 6
                }}>
                  <div>
                    <div style={{ fontSize: 7.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase" }}>Llegada Guardia</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginTop: 1 }}>
                      <span style={{ fontSize: 16, fontWeight: 800, color: "#3b82f6", fontFamily: "var(--mono)" }}>{avgArr ? fmtDur(avgArr) : "N/A"}</span>
                      {cmpArr && <span style={{ fontSize: 7.5, fontWeight: 800, color: cmpArr.color }}>{cmpArr.text}</span>}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                    <button
                      type="button"
                      className="panel-link-btn"
                      onClick={() => setActiveModal("guard")}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "var(--muted)",
                        fontSize: 9,
                        fontWeight: 700,
                        cursor: "pointer",
                        padding: "0 0 4px 0",
                        transition: "color 0.2s"
                      }}
                      onMouseEnter={e => e.target.style.color = "var(--bright)"}
                      onMouseLeave={e => e.target.style.color = "var(--muted)"}
                    >
                      Ver todo ↗
                    </button>
                    <div style={{ fontSize: 7.5, color: "var(--muted)", textTransform: "uppercase" }}>Prom. Lleg: <b style={{ color: "#3b82f6", fontFamily: "var(--mono)" }}>{avgArrivalTime ? fmtDur(avgArrivalTime) : "—"}</b></div>
                    <div style={{ fontSize: 7.5, color: "var(--muted)", marginTop: 1, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", maxWidth: 120 }}>Líder: <b style={{ color: "#a855f7" }}>{bestGuard.split(" ")[0]}</b></div>
                  </div>
                </div>

                {/* Guard table */}
                <div style={{ flex: 1, overflowY: "auto" }}>
                  {Object.keys(guardStats).length > 0 ? (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 30px 50px 45px 35px", gap: "4px 6px", fontSize: 9 }}>
                      <div style={TH}>Guardia</div><div style={TH}>Ev.</div><div style={TH}>T.lleg.</div><div style={TH}>Barras</div><div style={TH}>Exit.</div>
                      {(() => {
                        const sorted = Object.entries(guardStats).sort((a,b)=>b[1].ev - a[1].ev);
                        return sorted.map(([name, s]) => {
                          const aA = s.arr.length  ? Math.round(s.arr.reduce((a,b)=>a+b,0)  / s.arr.length)  : null;
                          const aB = s.bars.length ? (s.bars.reduce((a,b)=>a+b,0) / s.bars.length).toFixed(1) : null;
                          return (
                            <React.Fragment key={name}>
                              <div style={{ color: "var(--text)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                              <div style={{ color: "var(--subtle)", fontFamily: "var(--mono)", textAlign: "center" }}>{s.ev}</div>
                              <div style={{ color: aA&&aA<300?"#22c55e":aA&&aA<600?"#f59e0b":"#ef4444", fontFamily: "var(--mono)", textAlign: "center", fontWeight: 600 }}>{aA ? fmtDur(aA) : "—"}</div>
                              <div style={{ color: aB&&aB>=4?"#22c55e":aB&&aB>=3?"#f59e0b":"#ef4444", fontFamily: "var(--mono)", textAlign: "center", fontWeight: 600 }}>{aB || "—"}</div>
                              <div style={{ color: s.ok>0?"#22c55e":"var(--dim)", fontFamily: "var(--mono)", textAlign: "center", fontWeight: 700 }}>{s.ok}</div>
                            </React.Fragment>
                          );
                        });
                      })()}
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--muted)", fontSize: 9 }}>
                      Sin datos en este periodo
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div style={{ width: 288, display: "flex", flexDirection: "column", gap: 16, flexShrink: 0 }}>
            {renderResultsDistribution()}
              {/* PANEL E2: TOP ZONAS CRÍTICAS (Spans 2 columns, Row 2 left) */}
              <div style={{
                flex: 1,
                background: "var(--card)",
                border: "1px solid var(--border)",
                borderRadius: 12,
                padding: "12px 10px",
                minHeight: 300,
                display: "flex",
                flexDirection: "column"
              }}>
                <div style={{ marginBottom: 8, borderBottom: "1px solid var(--border)", paddingBottom: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: 7.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase" }}>Zonas Críticas</div>
                    <button
                      type="button"
                      className="panel-link-btn"
                      onClick={() => setActiveModal("zones")}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "var(--muted)",
                        fontSize: 9,
                        fontWeight: 700,
                        cursor: "pointer",
                        padding: "2px 6px",
                        borderRadius: 4,
                        transition: "color 0.2s"
                      }}
                      onMouseEnter={e => e.target.style.color = "var(--bright)"}
                      onMouseLeave={e => e.target.style.color = "var(--muted)"}
                    >
                      Ver todo ↗
                    </button>
                  </div>
                  <div style={{ fontSize: 9, fontWeight: 800, color: "var(--bright)", marginTop: 1 }}>Top Zonas con más Alertas</div>
                </div>

                <div style={{ width: "100%", flex: 1, position: "relative", minHeight: 0 }}>
                  {alertsByZone.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={alertsByZone.slice(0, 10)} layout="vertical" margin={{ top: 5, right: 22, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border)" />
                        <XAxis 
                          type="number"
                          axisLine={false} 
                          tickLine={false} 
                          tick={{ fontSize: 7.5, fill: 'var(--muted)', fontFamily: 'var(--mono)' }}
                          allowDecimals={false}
                        />
                        <YAxis 
                          type="category"
                          dataKey="name" 
                          axisLine={false} 
                          tickLine={false} 
                          tick={<ZoneAxisTick />}
                          width={70}
                          interval={0}
                        />
                        <Bar dataKey="value" fill="#a855f7" radius={[0, 4, 4, 0]} name="Alertas" barSize={9}>
                          <LabelList dataKey="value" position="right" style={{ fontSize: 7.5, fill: "var(--bright)", fontWeight: 700, fontFamily: "var(--mono)" }} />
                          {alertsByZone.slice(0, 10).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={index === 0 ? "#ef4444" : index === 1 ? "#f59e0b" : "#a855f7"} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--muted)", fontSize: 9 }}>
                      Sin datos de alertas por zona
                    </div>
                  )}
                </div>
              </div>
          </div>
        </div>
          );
        }

        return (
          <>
            <div className="kpi-grid-sup no-print" style={{ marginBottom: 16 }}>
            <KPI 
              label="Tasa de gestión"          
              value={`${managedPct}%`}             
              sub={`${managedByHuman} gestionados de ${totalNoDrill} eventos (Clic para ver tendencias)`}  
              color={managedPct >= 80 ? "#22c55e" : managedPct >= 50 ? "#f59e0b" : "#ef4444"} 
              compareText={cmpManaged.text} 
              compareColor={cmpManaged.color} 
              onClick={() => setActiveModal("activity")}
            />
            <KPI 
              label="Despacho CCTV"           
              value={avgDisp ? fmtDur(avgDisp) : "N/A"} 
              sub="Alerta → Despacho (Clic para rendimiento)"                               
              color="#f59e0b" 
              compareText={cmpDisp?.text} 
              compareColor={cmpDisp?.color} 
              onClick={() => setActiveModal("cctv")}
            />
            <KPI 
              label="Llegada guardia"         
              value={avgArr  ? fmtDur(avgArr)  : "N/A"} 
              sub="Despacho → Zona (Clic para rendimiento)"                                  
              color="#3b82f6" 
              compareText={cmpArr?.text} 
              compareColor={cmpArr?.color} 
              onClick={() => setActiveModal("guard")}
            />
            <KPI 
              label="Gestión con hallazgos"   
              value={`${findingsPct}%`}          
              sub={`${withFindings} de ${closed.length} cerrados (Clic para rendimiento)`} 
              color="#22c55e" 
              compareText={cmpFindings.text} 
              compareColor={cmpFindings.color} 
              onClick={() => setActiveModal("performance")}
            />
            <KPI 
              label="Nivel de actividad"      
              value={activityLevel.label}        
              sub={`${activitySub} (Clic para ver tendencias)`}                                              
              color={activityLevel.color} 
              compareText={cmpActivity?.text} 
              compareColor={cmpActivity?.color} 
              onClick={() => setActiveModal("activity")}
            />
            <KPI 
              label="Zona crítica"            
              value={criticalZone || "N/A"}      
              sub={criticalZone ? `${criticalMins} min · ${criticalPct}% del tiempo (Clic para ver heatmap)` : "Sin datos"} 
              color="#a855f7" 
              onClick={() => setActiveModal("zones")}
            />
          </div>
          {renderResultsDistribution()}
          {renderEventHistory()}
        </>
      );
    })()}



      {/* ── Modal Windows for detailed KPI information ── */}
      {activeModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(7, 10, 19, 0.85)", backdropFilter: "blur(12px)",
          zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
          padding: 16
        }}>
          <div style={{
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            width: "100%",
            maxWidth: activeModal === "history" ? 1500 : ["performance", "cctv", "guard"].includes(activeModal) ? 950 : 800,
            maxHeight: "90vh",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
            color: "var(--bright)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
          }}>
            {/* Modal Header */}
            <div style={{
              padding: "14px 20px",
              borderBottom: "1px solid var(--border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "var(--surface)"
            }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "var(--bright)" }}>
                {activeModal === "activity" && "Tendencia de Actividad y Resultados"}
                {activeModal === "zones" && "Análisis de Zonas e Inhibición"}
                {activeModal === "performance" && "Rendimiento de Guardias y Operadores"}
                {activeModal === "cctv" && "Rendimiento de Operadores CCTV"}
                {activeModal === "guard" && "Rendimiento de Guardias"}
                {activeModal === "duration" && "Distribución de Duración de Alertas"}
                {activeModal === "history" && "Historial de Eventos"}
              </div>
              <button
                type="button"
                onClick={() => setActiveModal(null)}
                style={{
                  background: "none", border: "none", color: "var(--muted)",
                  fontSize: 16, cursor: "pointer", padding: 4
                }}
              >
                ✕
              </button>
            </div>
            {/* Modal Body */}
            <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>
              {activeModal === "history" && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      {[
                        { key: "relevant", label: "⭐ Relevantes", color: "#eab308" },
                        { key: "findings", label: "✓ Con hallazgo", color: "#22c55e" },
                        { key: "plate",    label: "🚗 Con patente", color: "#3b82f6" },
                        { key: "suspect",  label: "👤 Con sospechoso", color: "#a855f7" },
                        ...(reviewRoles.canReview ? [
                          { key: "unreviewed", label: "👁 Sin revisar", color: "#94a3b8" },
                          { key: "validated",  label: "🛡 Validados", color: "#22c55e" },
                        ] : []),
                      ].map(f => {
                        const on = histFilters[f.key];
                        return (
                          <button
                            key={f.key}
                            type="button"
                            onClick={() => setHistFilters(s => ({ ...s, [f.key]: !s[f.key] }))}
                            style={{
                              fontSize: 9, fontWeight: 700, cursor: "pointer",
                              padding: "4px 9px", borderRadius: 999,
                              border: `1px solid ${on ? f.color : "var(--border)"}`,
                              background: on ? `${f.color}22` : "transparent",
                              color: on ? f.color : "var(--muted)",
                              transition: "all 0.15s"
                            }}
                          >
                            {f.label}
                          </button>
                        );
                      })}
                      {(histFilters.relevant || histFilters.findings || histFilters.plate || histFilters.suspect || histFilters.unreviewed || histFilters.validated) && (
                        <button
                          type="button"
                          onClick={() => setHistFilters({ relevant: false, findings: false, plate: false, suspect: false, unreviewed: false, validated: false })}
                          style={{
                            fontSize: 9, fontWeight: 700, cursor: "pointer",
                            padding: "4px 9px", borderRadius: 999,
                            border: "1px solid var(--border)", background: "transparent", color: "var(--muted)"
                          }}
                        >
                          ✕ Limpiar
                        </button>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ fontSize: 10, color: "var(--muted)", fontFamily: "var(--mono)" }}>
                        {modalHistoryRows.length} de {filtered.length}
                      </div>
                      {/* Entregable: aparece cuando la selección tiene casos validados */}
                      {reviewRoles.canReview && (() => {
                        const validated = modalHistoryRows.filter(a => getReviewState(a) === "validated");
                        if (validated.length === 0) return null;
                        return (
                          <button
                            type="button"
                            className="panel-link-btn"
                            onClick={() => setReportCases(validated)}
                            title="Ficha/reporte imprimible de los casos validados en esta selección"
                            style={{ background: "transparent", color: "#4ade80", fontSize: 9, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 3 }}
                          >
                            📄 Casos validados ({validated.length})
                          </button>
                        );
                      })()}
                      {isSupervisor && (
                        <button
                          type="button"
                          className="panel-link-btn"
                          onClick={() => handleExportCSV(modalHistoryRows)}
                          style={{ background: "transparent", color: "#22c55e", fontSize: 9, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 3 }}
                          onMouseEnter={e => e.currentTarget.style.color = "#4ade80"}
                          onMouseLeave={e => e.currentTarget.style.color = "#22c55e"}
                        >
                          📥 Excel
                        </button>
                      )}
                    </div>
                  </div>
                  {renderHistorySearch()}
                  {renderHistoryTable(modalHistoryRows, { minWidth: 1300, showComment: true, sort: histSort, onSort: toggleHistSort, showReview: reviewRoles.canReview })}
                </div>
              )}
              {activeModal === "activity" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {/* Activity Trend Chart with Previous Period Comparison */}
                  <div style={{
                    background: "var(--bg)",
                    border: "1px solid var(--border)",
                    borderRadius: 9,
                    padding: 16,
                    display: "flex",
                    flexDirection: "column"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 6 }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--bright)" }}>Tendencia de Actividad</div>
                        <div style={{ fontSize: 9, color: "var(--muted)", marginTop: 1 }}>Periodo actual vs. periodo anterior</div>
                      </div>
                      <div style={{ display: "flex", gap: 10, fontSize: 9, flexWrap: "wrap" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 3.5, color: "#3b82f6", fontWeight: 600 }}>
                          <span style={{ display: "inline-block", width: 14, height: 2, background: "#3b82f6", borderRadius: 1 }}></span>
                          Totales
                        </span>
                        <span style={{ display: "flex", alignItems: "center", gap: 3.5, color: "#22c55e", fontWeight: 600 }}>
                          <span style={{ display: "inline-block", width: 14, height: 2, background: "#22c55e", borderRadius: 1 }}></span>
                          Gestionadas
                        </span>
                        <span style={{ display: "flex", alignItems: "center", gap: 3.5, color: "#64748b", fontWeight: 600 }}>
                          <span style={{ display: "inline-block", width: 14, height: 2, background: "#64748b", borderRadius: 1, borderTop: "1px dashed #64748b" }}></span>
                          Periodo Ant.
                        </span>
                      </div>
                    </div>
                    <div style={{ width: "100%", height: 280, position: "relative" }}>
                      {modalReady && (
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={mergedChartData} margin={{ top: 10, right: 15, left: -25, bottom: 0 }}>
                            <defs>
                              <linearGradient id="modalColorTotal" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15}/>
                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.01}/>
                              </linearGradient>
                              <linearGradient id="modalColorGest" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.15}/>
                                <stop offset="95%" stopColor="#22c55e" stopOpacity={0.01}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                            <XAxis
                              dataKey="label"
                              axisLine={false}
                              tickLine={false}
                              tick={{ fontSize: 8.5, fill: 'var(--muted)', fontFamily: 'var(--mono)' }}
                            />
                            <YAxis
                              axisLine={false}
                              tickLine={false}
                              tick={{ fontSize: 8.5, fill: 'var(--muted)', fontFamily: 'var(--mono)' }}
                              allowDecimals={false}
                            />
                            <Tooltip
                              contentStyle={{
                                background: "var(--card)",
                                border: "1px solid var(--border)",
                                borderRadius: 6,
                                fontSize: 10,
                                color: "var(--text)"
                              }}
                              itemStyle={{ padding: "1px 0" }}
                            />
                            {/* Previous period - dashed lines behind */}
                            <Area type="monotone" dataKey="TotalPrev" stroke="#64748b" strokeWidth={1.5} strokeDasharray="5 3" fillOpacity={0} name="Total Ant." />
                            <Area type="monotone" dataKey="GestionadosPrev" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="5 3" fillOpacity={0} name="Gest. Ant." />
                            {/* Current period - solid lines on top */}
                            <Area type="monotone" dataKey="Total" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#modalColorTotal)" name="Alertas Totales">
                              <LabelList dataKey="Total" position="top" style={{ fontSize: 8, fill: "#3b82f6", fontWeight: 700, fontFamily: "var(--mono)" }} />
                            </Area>
                            <Area type="monotone" dataKey="Gestionados" stroke="#22c55e" strokeWidth={2} fillOpacity={1} fill="url(#modalColorGest)" name="Gestionadas">
                              <LabelList dataKey="Gestionados" position="top" style={{ fontSize: 8, fill: "#22c55e", fontWeight: 700, fontFamily: "var(--mono)" }} />
                            </Area>
                          </AreaChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeModal === "zones" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {/* Top Critical Zones chart */}
                  <div style={{
                    background: "var(--bg)",
                    border: "1px solid var(--border)",
                    borderRadius: 9,
                    padding: 16,
                    display: "flex",
                    flexDirection: "column"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--bright)" }}>Zonas Críticas</div>
                        <div style={{ fontSize: 9, color: "var(--muted)", marginTop: 1 }}>Zonas con mayor cantidad de alertas registradas en este periodo</div>
                      </div>
                      <div style={{ fontSize: 9, color: "var(--muted)", fontFamily: "var(--mono)" }}>
                        Mostrando todas las zonas ({alertsByZone.length})
                      </div>
                    </div>
                    {/* Add position: relative to parent div of ResponsiveContainer */}
                    <div style={{ width: "100%", height: Math.max(250, alertsByZone.length * 30), position: "relative" }}>
                      {alertsByZone.length > 0 && modalReady ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={alertsByZone} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border)" />
                            <XAxis 
                              type="number"
                              axisLine={false} 
                              tickLine={false} 
                              tick={{ fontSize: 8, fill: 'var(--muted)', fontFamily: 'var(--mono)' }}
                              allowDecimals={false}
                            />
                            <YAxis 
                              type="category"
                              dataKey="name" 
                              axisLine={false} 
                              tickLine={false} 
                              tick={{ fontSize: 8, fill: 'var(--subtle)', fontWeight: 600 }}
                              width={110}
                            />
                            <Tooltip
                              contentStyle={{
                                background: "var(--card)",
                                border: "1px solid var(--border)",
                                borderRadius: 6,
                                fontSize: 10,
                                color: "var(--text)"
                              }}
                            />
                            <Bar dataKey="value" fill="#a855f7" radius={[0, 4, 4, 0]} name="Alertas" barSize={10}>
                              <LabelList dataKey="value" position="right" style={{ fontSize: 8, fill: "var(--bright)", fontWeight: 700, fontFamily: "var(--mono)" }} />
                              {alertsByZone.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={index === 0 ? "#ef4444" : index === 1 ? "#f59e0b" : "#a855f7"} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : alertsByZone.length > 0 ? null : (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--muted)", fontSize: 11 }}>
                          Sin datos de alertas por zona en este periodo
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeModal === "duration" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ fontSize: 9.5, color: "var(--muted)" }}>
                      Frecuencia de eventos por tiempo de resolución de alerta.
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1, fontFamily: "var(--mono)", fontSize: 9, textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        <span style={{ color: "var(--muted)" }}>Filtro activo:</span>
                        <span style={{ fontWeight: 700, color: (minDurationFilter > 0 || maxDurationFilter < 1200) ? "#60a5fa" : "var(--bright)" }}>
                          {(() => {
                            const min = minDurationFilter;
                            const max = maxDurationFilter;
                            if (min === 0 && max === 1200) return "Todos";
                            const formatVal = (s) => {
                              if (s < 60) return `${s}s`;
                              const m = Math.floor(s / 60);
                              const r = s % 60;
                              return r === 0 ? `${m}m` : `${m}m ${r}s`;
                            };
                            if (min > 0 && max === 1200) return `>${formatVal(min)}`;
                            if (min === 0 && max < 1200) return `<=${formatVal(max)}`;
                            return `${formatVal(min)} a ${formatVal(max)}`;
                          })()}
                        </span>
                      </div>
                      {(minDurationFilter > 0 || maxDurationFilter < 1200) && (
                        <span style={{ fontSize: 8.5, color: "#60a5fa", fontWeight: 600 }}>
                          {filtered.length} de {baseFiltered.length} visibles
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Chart */}
                  <div style={{ width: "100%", height: 160, position: "relative", minHeight: 0, marginTop: 4 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={durationDistribution} margin={{ top: 10, right: 10, left: -25, bottom: -5 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                        <XAxis 
                          dataKey="name" 
                          axisLine={false} 
                          tick={false} 
                          tickLine={false} 
                        />
                        <YAxis 
                          axisLine={false} 
                          tickLine={false} 
                          tick={{ fontSize: 9, fill: 'var(--muted)', fontFamily: 'var(--mono)' }}
                          allowDecimals={false}
                        />
                        <Tooltip
                          contentStyle={{
                            background: "var(--card)",
                            border: "1px solid var(--border)",
                            borderRadius: 6,
                            fontSize: 10,
                            color: "var(--text)"
                          }}
                          formatter={(value, name, props) => [`${value} alertas`, `Rango: ${props.payload.rangeStart}s-${props.payload.rangeEnd === Infinity ? '∞' : props.payload.rangeEnd + 's'}`]}
                        />
                        <Bar dataKey="count" radius={[2, 2, 0, 0]} name="Alertas" barSize={8}>
                          {durationDistribution.map((entry, index) => {
                            const isFiltered = (minDurationFilter > 0 && entry.rangeEnd <= minDurationFilter) ||
                                               (maxDurationFilter < 1200 && entry.rangeStart >= maxDurationFilter);
                            return (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={isFiltered ? "rgba(255, 255, 255, 0.12)" : "#ffffff"} 
                              />
                            );
                          })}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Slider Control */}
                  <div style={{
                    marginTop: 16,
                    padding: "12px 16px",
                    background: "var(--bg)",
                    border: "1px solid var(--border)",
                    borderRadius: 8
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>
                      <span>Rango de duración</span>
                      <span style={{ color: "var(--bright)", fontWeight: 800 }}>Desliza para ajustar min/max</span>
                    </div>
                    
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 9, color: minDurationFilter === 0 ? "var(--bright)" : "var(--muted)", fontWeight: minDurationFilter === 0 ? 800 : 500, width: 30 }}>Todos</span>
                      
                      <div style={{ flex: 1, position: "relative", height: 30, display: "flex", alignItems: "center" }}>
                        {/* Background track */}
                        <div style={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          height: 5,
                          borderRadius: 2.5,
                          background: "var(--border)",
                          pointerEvents: "none"
                        }} />
                        
                        {/* Selected range highlight */}
                        <div style={{
                          position: "absolute",
                          left: `${(minDurationFilter / 1200) * 100}%`,
                          right: `${100 - (maxDurationFilter / 1200) * 100}%`,
                          height: 5,
                          borderRadius: 2.5,
                          background: "#3b82f6",
                          pointerEvents: "none"
                        }} />
                        
                        {/* Min range input */}
                        <input 
                          type="range"
                          min="0"
                          max="1200"
                          step="30"
                          value={minDurationFilter}
                          onFocus={() => setActiveThumb("min")}
                          onMouseEnter={() => setActiveThumb("min")}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            if (val <= maxDurationFilter) {
                              setMinDurationFilter(val);
                            }
                          }}
                          className="range-slider-input"
                          style={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            width: "100%",
                            height: 30,
                            margin: 0,
                            pointerEvents: "none",
                            background: "transparent",
                            zIndex: activeThumb === "min" ? 5 : 3
                          }}
                        />
                        
                        {/* Max range input */}
                        <input 
                          type="range"
                          min="0"
                          max="1200"
                          step="30"
                          value={maxDurationFilter}
                          onFocus={() => setActiveThumb("max")}
                          onMouseEnter={() => setActiveThumb("max")}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            if (val >= minDurationFilter) {
                              setMaxDurationFilter(val);
                            }
                          }}
                          className="range-slider-input"
                          style={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            width: "100%",
                            height: 30,
                            margin: 0,
                            pointerEvents: "none",
                            background: "transparent",
                            zIndex: activeThumb === "max" ? 5 : 3
                          }}
                        />
                      </div>

                      <span style={{ fontSize: 9, color: maxDurationFilter === 1200 ? "var(--bright)" : "var(--muted)", fontWeight: maxDurationFilter === 1200 ? 800 : 500, width: 30, textAlign: "right" }}>20m+</span>
                    </div>

                    {/* Scale markers */}
                    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 2px 0 2px", fontSize: 8, color: "var(--muted)", fontFamily: "var(--mono)" }}>
                      <span>Todos</span>
                      <span>5m</span>
                      <span>10m</span>
                      <span>15m</span>
                      <span>20m</span>
                    </div>
                  </div>
                </div>
              )}

              {["performance", "cctv", "guard"].includes(activeModal) && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {/* Findings Trend Chart - only in performance modal */}
                  {activeModal === "performance" && (
                    <div style={{
                      background: "var(--bg)",
                      border: "1px solid var(--border)",
                      borderRadius: 9,
                      padding: 16,
                      display: "flex",
                      flexDirection: "column"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 6 }}>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--bright)" }}>Eventos con Hallazgos vs. Total</div>
                          <div style={{ fontSize: 9, color: "var(--muted)", marginTop: 1 }}>Proporción de eventos cerrados con hallazgo en el periodo</div>
                        </div>
                        <div style={{ display: "flex", gap: 10, fontSize: 9 }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 3.5, color: "#64748b", fontWeight: 600 }}>
                            <span style={{ display: "inline-block", width: 14, height: 2, background: "#64748b", borderRadius: 1 }}></span>
                            Total Cerrados
                          </span>
                          <span style={{ display: "flex", alignItems: "center", gap: 3.5, color: "#22c55e", fontWeight: 600 }}>
                            <span style={{ display: "inline-block", width: 14, height: 2, background: "#22c55e", borderRadius: 1 }}></span>
                            Con Hallazgos
                          </span>
                        </div>
                      </div>
                      <div style={{ width: "100%", height: 220, position: "relative" }}>
                        {modalReady && (
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={findingsChartData} margin={{ top: 10, right: 15, left: -25, bottom: 0 }}>
                              <defs>
                                <linearGradient id="findingsColorTotal" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#64748b" stopOpacity={0.1}/>
                                  <stop offset="95%" stopColor="#64748b" stopOpacity={0.01}/>
                                </linearGradient>
                                <linearGradient id="findingsColorHallazgo" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.2}/>
                                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0.01}/>
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 8.5, fill: 'var(--muted)', fontFamily: 'var(--mono)' }} />
                              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 8.5, fill: 'var(--muted)', fontFamily: 'var(--mono)' }} allowDecimals={false} />
                              <Tooltip contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 6, fontSize: 10, color: "var(--text)" }} itemStyle={{ padding: "1px 0" }} />
                              <Area type="monotone" dataKey="Total" stroke="#64748b" strokeWidth={1.5} fillOpacity={1} fill="url(#findingsColorTotal)" name="Total Cerrados" />
                              <Area type="monotone" dataKey="ConHallazgos" stroke="#22c55e" strokeWidth={2} fillOpacity={1} fill="url(#findingsColorHallazgo)" name="Con Hallazgos">
                                <LabelList dataKey="ConHallazgos" position="top" style={{ fontSize: 8, fill: "#22c55e", fontWeight: 700, fontFamily: "var(--mono)" }} />
                              </Area>
                            </AreaChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Performance KPIs Grid */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8 }}>
                    {activeModal !== "guard" && (
                      <div style={{
                        background: "linear-gradient(135deg, #0f1624 0%, #192239 100%)",
                        border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px", borderLeft: "4px solid #f59e0b"
                      }}>
                        <div style={{ fontSize: 8.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase" }}>T. Promedio Respuesta CCTV</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: "#f59e0b", fontFamily: "var(--mono)", marginTop: 2 }}>
                          {avgReactionTime ? fmtDur(avgReactionTime) : "—"}
                        </div>
                        <div style={{ fontSize: 8, color: "var(--muted)", marginTop: 2 }}>Tiempo desde Alerta hasta Despacho</div>
                      </div>
                    )}

                    {activeModal !== "cctv" && (
                      <div style={{
                        background: "linear-gradient(135deg, #0f1624 0%, #192239 100%)",
                        border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px", borderLeft: "4px solid #3b82f6"
                      }}>
                        <div style={{ fontSize: 8.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase" }}>T. Promedio Llegada Guardia</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: "#3b82f6", fontFamily: "var(--mono)", marginTop: 2 }}>
                          {avgArrivalTime ? fmtDur(avgArrivalTime) : "—"}
                        </div>
                        <div style={{ fontSize: 8, color: "var(--muted)", marginTop: 2 }}>Tiempo desde Despacho hasta arribar a Zona</div>
                      </div>
                    )}

                    {activeModal !== "guard" && (
                      <div style={{
                        background: "linear-gradient(135deg, #0f1624 0%, #192239 100%)",
                        border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px", borderLeft: "4px solid #22c55e"
                      }}>
                        <div style={{ fontSize: 8.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase" }}>Respuesta CCTV Más Rápida</div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: "#22c55e", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {bestOperator}
                        </div>
                        <div style={{ fontSize: 8, color: "var(--muted)", marginTop: 4 }}>Operador CCTV líder en velocidad</div>
                      </div>
                    )}

                    {activeModal === "performance" && (
                      <div style={{
                        background: "linear-gradient(135deg, #0f1624 0%, #192239 100%)",
                        border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px", borderLeft: "4px solid #a855f7"
                      }}>
                        <div style={{ fontSize: 8.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase" }}>Desplazamiento Más Rápido</div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: "#a855f7", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {bestGuard}
                        </div>
                        <div style={{ fontSize: 8, color: "var(--muted)", marginTop: 4 }}>Guardia con menor tiempo de llegada</div>
                      </div>
                    )}

                    {activeModal === "guard" && (
                      <div style={{
                        background: "linear-gradient(135deg, #0f1624 0%, #192239 100%)",
                        border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px", borderLeft: "4px solid #22c55e"
                      }}>
                        <div style={{ fontSize: 8.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase" }}>Guardia con más asistencias</div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: "#22c55e", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {topGuardByAttendance ? topGuardByAttendance.name : "Sin datos"}
                        </div>
                        <div style={{ fontSize: 8, color: "var(--muted)", marginTop: 4 }}>
                          {topGuardByAttendance ? `${topGuardByAttendance.count} evento${topGuardByAttendance.count === 1 ? "" : "s"} atendido${topGuardByAttendance.count === 1 ? "" : "s"}` : "—"}
                        </div>
                      </div>
                    )}

                    {activeModal === "guard" && (
                      <div style={{
                        background: "linear-gradient(135deg, #0f1624 0%, #192239 100%)",
                        border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px", borderLeft: "4px solid #eab308"
                      }}>
                        <div style={{ fontSize: 8.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase" }}>Guardia con más hallazgos</div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: "#eab308", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {topGuardByFindings ? topGuardByFindings.name : "Sin datos"}
                        </div>
                        <div style={{ fontSize: 8, color: "var(--muted)", marginTop: 4 }}>
                          {topGuardByFindings ? `${topGuardByFindings.count} con hallazgo (Exit.)` : "—"}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Performance Tables */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))", gap: 12 }}>
                    {/* Guard performance table */}
                    {Object.keys(guardStats).length > 0 && activeModal !== "cctv" && (
                      <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 9, padding: 12, display: "flex", flexDirection: "column" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--bright)" }}>Rendimiento por guardia</div>
                          <div style={{ fontSize: 8.5, color: "var(--muted)" }}>
                            {Object.keys(guardStats).length} guardias
                          </div>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 50px 70px 60px 50px", gap: "4px 6px", fontSize: 10, flex: 1 }}>
                          <div style={TH}>Guardia</div><div style={TH}>Ev.</div><div style={TH}>T.lleg.</div><div style={TH}>Barras</div><div style={TH}>Exit.</div>
                          {(() => {
                            const sorted = Object.entries(guardStats).sort((a,b)=>b[1].ev - a[1].ev);
                            const list = sorted;
                            return list.map(([name, s]) => {
                              const aA = s.arr.length  ? Math.round(s.arr.reduce((a,b)=>a+b,0)  / s.arr.length)  : null;
                              const aB = s.bars.length ? (s.bars.reduce((a,b)=>a+b,0) / s.bars.length).toFixed(1) : null;
                              return (
                                <React.Fragment key={name}>
                                  <div style={{ color: "var(--text)", fontWeight: 600, overflow: "visible", whiteSpace: "normal", lineHeight: 1.3, wordBreak: "break-word" }}>{name}</div>
                                  <div style={{ color: "var(--subtle)", fontFamily: "var(--mono)", textAlign: "center" }}>{s.ev}</div>
                                  <div style={{ color: aA&&aA<300?"#22c55e":aA&&aA<600?"#f59e0b":"#ef4444", fontFamily: "var(--mono)", textAlign: "center", fontWeight: 600 }}>{aA ? fmtDur(aA) : "—"}</div>
                                  <div style={{ color: aB&&aB>=4?"#22c55e":aB&&aB>=3?"#f59e0b":"#ef4444", fontFamily: "var(--mono)", textAlign: "center", fontWeight: 600 }}>{aB || "—"}</div>
                                  <div style={{ color: s.ok>0?"#22c55e":"var(--dim)", fontFamily: "var(--mono)", textAlign: "center", fontWeight: 700 }}>{s.ok}</div>
                                </React.Fragment>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Operator table */}
                    {Object.keys(opStats).length > 0 && activeModal !== "guard" && (
                      <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 9, padding: 12, display: "flex", flexDirection: "column" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--bright)" }}>Rendimiento operadores CCTV</div>
                          <div style={{ fontSize: 8.5, color: "var(--muted)" }}>
                            {Object.keys(opStats).length} operadores
                          </div>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 70px 70px 60px 70px", gap: "4px 8px", fontSize: 10, flex: 1 }}>
                          <div style={TH}>Operador</div>
                          <div style={{ ...TH, textAlign: "center" }}>Despachos</div>
                          <div style={{ ...TH, textAlign: "center" }}>Cierres</div>
                          <div style={{ ...TH, textAlign: "center" }}>Exitosos</div>
                          <div style={{ ...TH, textAlign: "center" }}>T. Reac.</div>
                          {(() => {
                            const sorted = Object.entries(opStats).sort((a,b)=>b[1].disp - a[1].disp);
                            const list = sorted;
                            return list.map(([n, s]) => {
                              const avgReact = s.reactionTimes && s.reactionTimes.length
                                ? Math.round(s.reactionTimes.reduce((a, b) => a + b, 0) / s.reactionTimes.length)
                                : null;
                              return (
                                <React.Fragment key={n}>
                                  <div style={{ color: "var(--text)", fontWeight: 600, overflow: "visible", whiteSpace: "normal", lineHeight: 1.3, wordBreak: "break-word" }}>{n}</div>
                                  <div style={{ color: "var(--subtle)", fontFamily: "var(--mono)", textAlign: "center" }}>{s.disp}</div>
                                  <div style={{ color: "var(--subtle)", fontFamily: "var(--mono)", textAlign: "center" }}>{s.close}</div>
                                  <div style={{ color: s.ok>0?"#22c55e":"var(--dim)", fontFamily: "var(--mono)", textAlign: "center", fontWeight: 700 }}>{s.ok}</div>
                                  <div style={{ color: avgReact&&avgReact<60?"#22c55e":avgReact&&avgReact<180?"#f59e0b":"#ef4444", fontFamily: "var(--mono)", textAlign: "center", fontWeight: 600 }}>
                                    {avgReact ? fmtDur(avgReact) : "—"}
                                  </div>
                                </React.Fragment>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── PDF Export Only Container (Hidden on screen, visible only to html2canvas during export) ── */}
      <div className="pdf-slides-export-container">
        <div id="pdf-slide-1">
        {/* Header + period selector */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 12,
        gap: 8,
        width: "100%",
        marginTop: saving ? 0 : 4,
        paddingTop: saving ? 0 : 4,
        overflow: "visible"
      }}>
        <div style={{ minWidth: 0, flex: 1, overflow: "visible" }}>
          <div style={{
            fontSize: 16,
            fontWeight: 800,
            color: "var(--bright)",
            lineHeight: 1.6,
            overflow: "visible",
            paddingTop: "2px",
            paddingBottom: "2px"
          }}>Panel Supervisor</div>
          <div className="supervisor-subtitle" style={{ fontSize: 11, color: "var(--muted)", overflow: "visible" }}>
            <span>Eventos de {periodLabel} · {filtered.length} total</span>
            {facility?.visibleHoursRange ? (
              <span style={{
                fontSize: 8.5,
                fontWeight: 600,
                background: "rgba(34, 197, 94, 0.08)",
                border: "1px solid rgba(34, 197, 94, 0.2)",
                color: "#22c55e",
                padding: "1px 6px",
                borderRadius: 10,
                display: "inline-flex",
                alignItems: "center",
                gap: 3.5,
              }}>
                <span style={{ display: "inline-block", width: 4.5, height: 4.5, borderRadius: "50%", background: "#22c55e" }}></span>
                Turno: {String(facility.visibleHoursRange[0]).padStart(2, "0")}:00 - {String(facility.visibleHoursRange[1]).padStart(2, "0")}:00
              </span>
            ) : (
              <span style={{
                fontSize: 8.5,
                fontWeight: 600,
                background: "var(--surface)",
                border: "1px solid var(--border)",
                color: "var(--muted)",
                padding: "1px 6px",
                borderRadius: 10,
                display: "inline-flex",
                alignItems: "center",
                gap: 3.5,
              }}>
                <span style={{ display: "inline-block", width: 4.5, height: 4.5, borderRadius: "50%", background: "var(--muted)" }}></span>
                24 hrs
              </span>
            )}
          </div>
        </div>
        <div className="no-print" style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <div style={{
            display: "flex",
            gap: 2,
            background: "var(--bg)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            padding: 2
          }}>
            {[["day", "Hoy"], ["week", "Semana"], ["month", "Mes"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setPeriod(k)} style={{
                padding: "4px 10px", borderRadius: 4, border: "none",
                background: period === k ? "var(--surface)" : "transparent",
                color: period === k ? "var(--bright)" : "var(--muted)",
                fontSize: 10, fontWeight: 600, cursor: "pointer",
                transition: "background 0.15s, color 0.15s"
              }}>{l}</button>
            ))}
          </div>
          {reviewRoles.canReview && (
            <button
              type="button"
              onClick={() => setShowInbox(true)}
              title="Bandeja de revisión de eventos"
              style={{
                padding: "5px 10px", borderRadius: 6,
                border: `1px solid ${pendingReviewCount > 0 ? "#f59e0b55" : "var(--border)"}`,
                background: pendingReviewCount > 0 ? "#f59e0b18" : "var(--surface)",
                color: pendingReviewCount > 0 ? "#fbbf24" : "var(--bright)",
                fontSize: 10, fontWeight: 600, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 5,
              }}
            >
              📋 Revisión
              {pendingReviewCount > 0 && (
                <span style={{ background: "#f59e0b", color: "#1a1205", borderRadius: 999, fontSize: 8.5, padding: "1px 6px", fontWeight: 800 }}>
                  {pendingReviewCount}
                </span>
              )}
            </button>
          )}
          {(isSupervisor || isDevUser) && (
            <button
              type="button"
              onClick={() => setShowSettings(true)}
              style={{
                padding: "5px 10px",
                borderRadius: 6,
                border: "1px solid var(--border)",
                background: "var(--surface)",
                color: "var(--bright)",
                fontSize: 10,
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4
              }}
            >
              ⚙️ Ajustes
            </button>
          )}
        </div>
      </div>

      {/* KPIs – 6 cards */}
      {(() => {
        const cmpManaged = (() => {
          const diff = managedPct - prevStats.managedPct;
          if (diff > 0) return { text: `▲ +${diff}%`, color: "#22c55e" };
          if (diff < 0) return { text: `▼ ${diff}%`, color: "#ef4444" };
          return { text: "= vs ant.", color: "var(--muted)" };
        })();

        const cmpDisp = (() => {
          if (!avgDisp || !prevStats.avgDisp) return null;
          const pct = Math.round((avgDisp - prevStats.avgDisp) / prevStats.avgDisp * 100);
          if (pct < 0) return { text: `▼ ${pct}%`, color: "#22c55e" };
          if (pct > 0) return { text: `▲ +${pct}%`, color: "#ef4444" };
          return { text: "= vs ant.", color: "var(--muted)" };
        })();

        const cmpArr = (() => {
          if (!avgArr || !prevStats.avgArr) return null;
          const pct = Math.round((avgArr - prevStats.avgArr) / prevStats.avgArr * 100);
          if (pct < 0) return { text: `▼ ${pct}%`, color: "#22c55e" };
          if (pct > 0) return { text: `▲ +${pct}%`, color: "#ef4444" };
          return { text: "= vs ant.", color: "var(--muted)" };
        })();

        const cmpFindings = (() => {
          const diff = findingsPct - prevStats.findingsPct;
          if (diff > 0) return { text: `▲ +${diff}%`, color: "#22c55e" };
          if (diff < 0) return { text: `▼ ${diff}%`, color: "#ef4444" };
          return { text: "= vs ant.", color: "var(--muted)" };
        })();

        const cmpActivity = (() => {
          if (!prevStats.minutesTotal) return null;
          const pct = Math.round((currentMinutes - prevStats.minutesTotal) / prevStats.minutesTotal * 100);
          if (pct < 0) return { text: `▼ ${pct}%`, color: "#22c55e" };
          if (pct > 0) return { text: `▲ +${pct}%`, color: "#ef4444" };
          return { text: "= vs ant.", color: "var(--muted)" };
        })();

        return (
          <div className="kpi-grid-sup" style={{ marginBottom: 12 }}>
            <KPI label="Tasa de gestión"          value={`${managedPct}%`}             sub={`${managedByHuman} gestionados de ${totalNoDrill} eventos`}  color={managedPct >= 80 ? "#22c55e" : managedPct >= 50 ? "#f59e0b" : "#ef4444"} compareText={cmpManaged.text} compareColor={cmpManaged.color} />
            <KPI label="Despacho CCTV"           value={avgDisp ? fmtDur(avgDisp) : "N/A"} sub="Alerta → Despacho"                               color="#f59e0b" compareText={cmpDisp?.text} compareColor={cmpDisp?.color} />
            <KPI label="Llegada guardia"         value={avgArr  ? fmtDur(avgArr)  : "N/A"} sub="Despacho → Zona"                                  color="#3b82f6" compareText={cmpArr?.text} compareColor={cmpArr?.color} />
            <KPI label="Gestión con hallazgos"   value={`${findingsPct}%`}          sub={`${withFindings} de ${closed.length} cerrados con al menos un hallazgo`} color="#22c55e" compareText={cmpFindings.text} compareColor={cmpFindings.color} />
            <KPI label="Nivel de actividad"      value={activityLevel.label}        sub={activitySub}                                              color={activityLevel.color} compareText={cmpActivity?.text} compareColor={cmpActivity?.color} />
            <KPI label="Zona crítica"            value={criticalZone || "N/A"}      sub={criticalZone ? `${criticalMins} min acumulados · ${criticalPct}% del tiempo total ${periodLabel}` : "Sin datos"} color="#a855f7" />
          </div>
        );
      })()}

      {/* Charts Grid */}
      <div className="supervisor-charts-grid">
        {/* Temporal trend chart */}
        <div style={{
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: 9,
          padding: 12,
          display: "flex",
          flexDirection: "column"
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--subtle)" }}>Tendencia de Actividad</div>
              <div style={{ fontSize: 9, color: "var(--muted)", marginTop: 1 }}>Alertas totales vs. gestionadas por operadores</div>
            </div>
            {/* Legend indicators */}
            <div style={{ display: "flex", gap: 10, fontSize: 9 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 3.5, color: "#3b82f6", fontWeight: 600 }}>
                <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#3b82f6" }}></span>
                Totales
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 3.5, color: "#22c55e", fontWeight: 600 }}>
                <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#22c55e" }}></span>
                Gestionadas
              </span>
            </div>
          </div>
          <div style={{ width: "100%", height: 370 }}>
            <AreaChart width={610} height={370} data={chartData} margin={{ top: 10, right: 15, left: -25, bottom: 0 }}>
              <defs>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.01}/>
                </linearGradient>
                <linearGradient id="colorGest" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.15}/>
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0.01}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
              <XAxis 
                dataKey="label" 
                axisLine={false} 
                tickLine={false} 
                tick={{ fontSize: 8.5, fill: 'var(--muted)', fontFamily: 'var(--mono)' }} 
              />
              <YAxis 
                axisLine={false} 
                tickLine={false} 
                tick={{ fontSize: 8.5, fill: 'var(--muted)', fontFamily: 'var(--mono)' }}
                allowDecimals={false}
              />
              {!saving && (
                <Tooltip
                  contentStyle={{
                    background: "var(--card)",
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                    fontSize: 10,
                    color: "var(--text)"
                  }}
                  itemStyle={{ padding: "1px 0" }}
                />
              )}
              <Area type="monotone" dataKey="Total" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorTotal)" name="Alertas Totales" isAnimationActive={false}>
                <LabelList dataKey="Total" position="top" style={{ fontSize: 8, fill: "#3b82f6", fontWeight: 700, fontFamily: "var(--mono)" }} />
              </Area>
              <Area type="monotone" dataKey="Gestionados" stroke="#22c55e" strokeWidth={2} fillOpacity={1} fill="url(#colorGest)" name="Gestionadas" isAnimationActive={false}>
                <LabelList dataKey="Gestionados" position="top" style={{ fontSize: 8, fill: "#22c55e", fontWeight: 700, fontFamily: "var(--mono)" }} />
              </Area>
            </AreaChart>
          </div>
        </div>

        {/* Results distribution bar */}
        {Object.keys(byResult).length > 0 ? (
          <div style={{
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 9,
            padding: 12,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center"
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--subtle)", marginBottom: 12 }}>Distribución de resultados</div>
            {saving ? (
              <div style={{ width: "100%", height: 260, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <PieChart width={360} height={260}>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={75}
                    paddingAngle={3}
                    dataKey="value"
                    label={{ fill: "var(--bright)", fontSize: 9, fontWeight: 700 }}
                    isAnimationActive={false}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={R_COLORS[entry.key] || "#64748b"} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: "var(--card)",
                      border: "1px solid var(--border)",
                      borderRadius: 6,
                      fontSize: 10,
                      color: "var(--text)"
                    }}
                  />
                </PieChart>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 3, height: 22, borderRadius: 5, overflow: "hidden", marginBottom: 12 }}>
                {Object.entries(byResult).map(([k, c]) => (
                  <div key={k} style={{ flex: c, background: R_COLORS[k] || "#64748b", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "#fff" }}>{c}</div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: "8px 12px", flexWrap: "wrap", justifyContent: "center" }}>
              {Object.entries(R_LABELS).map(([k, l]) => {
                if (saving && !byResult[k]) return null;
                return (
                  <span key={k} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: "var(--subtle)" }}>
                    <span style={{ width: 7, height: 7, borderRadius: 2, background: R_COLORS[k] }} />
                    {l}: <b>{byResult[k] || 0}</b>
                  </span>
                );
              })}
            </div>
          </div>
        ) : (
          <div style={{
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 9,
            padding: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--muted)",
            fontSize: 11
          }}>
            Sin distribución de resultados en este periodo
          </div>
        )}
      </div>
      </div> {/* pdf-slide-1 */}

      <div id="pdf-slide-2">
        {/* Slide 2 Header (Looker Studio style) */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
          borderBottom: "1px solid var(--border)",
          paddingBottom: 8,
          width: "100%"
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "var(--bright)" }}>Análisis Temporal y Geográfico de Alertas</div>
            <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>
              {facility?.name || "Recinto"} · Eventos de {periodLabel}
            </div>
          </div>
          <div style={{ fontSize: 9, color: "var(--muted)", fontFamily: "var(--mono)" }}>
            Generado: {new Date().toLocaleDateString("es-CL")}
          </div>
        </div>

        {/* Heatmap & Critical Zones Layout */}
        <div className="pdf-slide-2-charts-grid">
          {/* Heatmap */}
          <div style={{
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 9,
            padding: 16,
            display: "flex",
            flexDirection: "column",
            flex: 1
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--subtle)" }}>Heatmap de Inhibición (Tiempo de Señal)</div>
              {selectedHeatmapCell && (
                <button
                  className="no-print"
                  onClick={() => setSelectedHeatmapCell(null)}
                  style={{
                    background: "rgba(239, 68, 68, 0.15)",
                    color: "#ef4444",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    borderRadius: 4,
                    padding: "2px 8px",
                    fontSize: 9,
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4
                  }}
                >
                  ✕ Quitar Filtro
                </button>
              )}
            </div>
            <div style={{ fontSize: 9, color: "var(--muted)", marginTop: 1, marginBottom: 12 }}>Cruces de tramos horarios vs. días de la semana (Lunes a Domingo)</div>
            
            {heatmapData.maxVal > 0 ? (() => {
              const { grid, maxVal } = heatmapData;
              const daysHeader = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
              return (
                <div style={{ display: "flex", flexDirection: "column", flex: 1, justifyContent: "center" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "50px repeat(7, 1fr)", gap: 4, textAlign: "center", marginBottom: 6 }}>
                    <div></div>
                    {daysHeader.map(d => (
                      <div key={d} style={{ fontSize: 9, fontWeight: 700, color: "var(--subtle)" }}>{d}</div>
                    ))}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                    {grid.map((row) => (
                      <div key={row.label} style={{ display: "grid", gridTemplateColumns: "50px repeat(7, 1fr)", gap: 4, alignItems: "center" }}>
                        <div style={{ fontSize: 8.5, color: "var(--muted)", fontFamily: "var(--mono)", textAlign: "right", paddingRight: 6 }}>{row.label}</div>
                        {row.days.map((val, dIdx) => {
                          const isSelected = selectedHeatmapCell && selectedHeatmapCell.rowIdx === row.rowIdx && selectedHeatmapCell.dayIdx === dIdx;
                          const anySelected = !!selectedHeatmapCell;
                          const ratio = maxVal > 0 ? val / maxVal : 0;
                          const opacity = val > 0 ? 0.15 + ratio * 0.85 : 0.03;
                          
                          let bg = val > 0 ? `rgba(239, 68, 68, ${opacity})` : "rgba(255, 255, 255, 0.02)";
                          let border = val > 0 ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid var(--border)";
                          
                          if (anySelected) {
                            if (isSelected) {
                              border = "2px solid #3b82f6";
                            } else {
                              bg = val > 0 ? `rgba(239, 68, 68, ${opacity * 0.3})` : "rgba(255, 255, 255, 0.005)";
                              border = val > 0 ? "1px solid rgba(239, 68, 68, 0.1)" : "1px solid rgba(255, 255, 255, 0.02)";
                            }
                          }
                          const textColor = val > 0 && ratio > 0.55 ? "#fff" : "var(--muted)";
                          return (
                            <div
                              key={dIdx}
                              title={`Día: ${daysHeader[dIdx]} · Tramo: ${row.label} · Inhibición: ${fmtDur(val)}`}
                              onClick={() => {
                                if (val > 0) {
                                  if (isSelected) {
                                    setSelectedHeatmapCell(null);
                                  } else {
                                    setSelectedHeatmapCell({ rowIdx: row.rowIdx, dayIdx: dIdx });
                                  }
                                }
                              }}
                              style={{
                                background: bg,
                                border: border,
                                borderRadius: 4,
                                height: 26,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 8,
                                fontFamily: "var(--mono)",
                                fontWeight: 700,
                                color: isSelected ? "#3b82f6" : textColor,
                                cursor: val > 0 ? "pointer" : "default",
                                opacity: anySelected && !isSelected ? 0.4 : 1,
                                boxShadow: isSelected ? "0 0 0 2px rgba(59, 130, 246, 0.3)" : "none",
                                transition: "all 0.2s ease"
                              }}
                            >
                              {fmtHeatVal(val)}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, fontSize: 8.5, color: "var(--muted)" }}>
                    <span>* Celdas muestran tiempo total de inhibición acumulado</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span>Menos</span>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: "rgba(255, 255, 255, 0.02)", border: "1px solid var(--border)" }} />
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: "rgba(239, 68, 68, 0.2)" }} />
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: "rgba(239, 68, 68, 0.5)" }} />
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: "rgba(239, 68, 68, 0.8)" }} />
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: "rgba(239, 68, 68, 1)" }} />
                      <span>Más</span>
                    </div>
                  </div>
                </div>
              );
            })() : (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", minHeight: 200, color: "var(--muted)", fontSize: 11 }}>
                Sin registro de inhibiciones en este periodo
              </div>
            )}
          </div>

          {/* Top 5 Critical Zones */}
          <div style={{
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 9,
            padding: 16,
            display: "flex",
            flexDirection: "column",
            flex: 1
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--subtle)" }}>Top 15 Zonas Críticas</div>
            <div style={{ fontSize: 9, color: "var(--muted)", marginTop: 1, marginBottom: 12 }}>Zonas con mayor cantidad de alertas registradas en este periodo</div>
            <div style={{ width: "100%", height: 500 }}>
              {alertsByZone.length > 0 ? (
                <BarChart width={450} height={500} data={alertsByZone} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border)" />
                  <XAxis 
                    type="number"
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fontSize: 8, fill: 'var(--muted)', fontFamily: 'var(--mono)' }}
                    allowDecimals={false}
                  />
                  <YAxis 
                    type="category"
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fontSize: 8, fill: 'var(--subtle)', fontWeight: 600 }}
                    width={110}
                  />
                  {!saving && (
                    <Tooltip
                      contentStyle={{
                        background: "var(--card)",
                        border: "1px solid var(--border)",
                        borderRadius: 6,
                        fontSize: 10,
                        color: "var(--text)"
                      }}
                    />
                  )}
                  <Bar dataKey="value" fill="#a855f7" radius={[0, 4, 4, 0]} name="Alertas" barSize={10} isAnimationActive={false}>
                    <LabelList dataKey="value" position="right" style={{ fontSize: 8, fill: "var(--bright)", fontWeight: 700, fontFamily: "var(--mono)" }} />
                    {alertsByZone.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={index === 0 ? "#ef4444" : index === 1 ? "#f59e0b" : "#a855f7"} />
                    ))}
                  </Bar>
                </BarChart>
              ) : (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--muted)", fontSize: 11 }}>
                  Sin datos de alertas por zona en este periodo
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div id="pdf-slide-3">
        {/* Slide 3 Header (Looker Studio style) */}
        <div className="only-print-presentation" style={{
          display: "none",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
          borderBottom: "1px solid var(--border)",
          paddingBottom: 8,
          width: "100%"
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "var(--bright)" }}>Reporte de Rendimiento</div>
            <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>
              {facility?.name || "Recinto"} · Eventos de {periodLabel}
            </div>
          </div>
          <div style={{ fontSize: 9, color: "var(--muted)", fontFamily: "var(--mono)" }}>
            Generado: {new Date().toLocaleDateString("es-CL")}
          </div>
        </div>

        {/* Performance indicators grid */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 10,
          marginBottom: 14,
          width: "100%"
        }}>
          {/* Card 1: CCTV Reaction Time */}
          <div style={{
            background: "linear-gradient(135deg, #0f1624 0%, #192239 100%)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: "10px 12px",
            borderLeft: "4px solid #f59e0b",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
          }}>
            <div style={{ fontSize: 8.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".04em" }}>T. Promedio Respuesta CCTV</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#f59e0b", fontFamily: "var(--mono)", marginTop: 2 }}>
              {avgReactionTime ? fmtDur(avgReactionTime) : "—"}
            </div>
            <div style={{ fontSize: 8, color: "var(--muted)", marginTop: 2 }}>Tiempo desde Alerta hasta Despacho</div>
          </div>

          {/* Card 2: Guard Arrival Time */}
          <div style={{
            background: "linear-gradient(135deg, #0f1624 0%, #192239 100%)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: "10px 12px",
            borderLeft: "4px solid #3b82f6",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
          }}>
            <div style={{ fontSize: 8.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".04em" }}>T. Promedio Llegada Guardia</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#3b82f6", fontFamily: "var(--mono)", marginTop: 2 }}>
              {avgArrivalTime ? fmtDur(avgArrivalTime) : "—"}
            </div>
            <div style={{ fontSize: 8, color: "var(--muted)", marginTop: 2 }}>Tiempo desde Despacho hasta arribar a Zona</div>
          </div>

          {/* Card 3: CCTV Leader */}
          <div style={{
            background: "linear-gradient(135deg, #0f1624 0%, #192239 100%)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: "10px 12px",
            borderLeft: "4px solid #22c55e",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
          }}>
            <div style={{ fontSize: 8.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".04em" }}>Respuesta Más Rápida (CCTV)</div>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#22c55e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 4 }}>
              {bestOperator}
            </div>
            <div style={{ fontSize: 8, color: "var(--muted)", marginTop: 4 }}>Operador CCTV líder en velocidad</div>
          </div>

          {/* Card 4: Guard Leader */}
          <div style={{
            background: "linear-gradient(135deg, #0f1624 0%, #192239 100%)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: "10px 12px",
            borderLeft: "4px solid #a855f7",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
          }}>
            <div style={{ fontSize: 8.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".04em" }}>Desplazamiento Más Rápido</div>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#a855f7", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 4 }}>
              {bestGuard}
            </div>
            <div style={{ fontSize: 8, color: "var(--muted)", marginTop: 4 }}>Guardia con menor tiempo de llegada</div>
          </div>
        </div>

        {/* Performance tables side-by-side on desktop */}
        <div className="supervisor-tables-grid">
        {/* Guard performance table */}
        {Object.keys(guardStats).length > 0 && (
          <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 9, padding: 12, overflowX: "auto" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--subtle)", marginBottom: 8 }}>Rendimiento por guardia</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 50px 70px 60px 50px", gap: "4px 6px", fontSize: 10, minWidth: 380 }}>
              <div style={TH}>Guardia</div><div style={TH}>Ev.</div><div style={TH}>T.lleg.</div><div style={TH}>Barras</div><div style={TH}>Exit.</div>
              {Object.entries(guardStats).sort((a, b) => b[1].ev - a[1].ev).map(([name, s]) => {
                const aA = s.arr.length  ? Math.round(s.arr.reduce((a,b)=>a+b,0)  / s.arr.length)  : null;
                const aB = s.bars.length ? (s.bars.reduce((a,b)=>a+b,0) / s.bars.length).toFixed(1) : null;
                return (
                  <React.Fragment key={name}>
                    <div style={{ color: "var(--text)", fontWeight: 600, overflow: "visible", textOverflow: "clip", whiteSpace: "normal", lineHeight: 1.3, wordBreak: "break-word" }}>{name}</div>
                    <div style={{ color: "var(--subtle)", fontFamily: "var(--mono)", textAlign: "center" }}>{s.ev}</div>
                    <div style={{ color: aA&&aA<300?"#22c55e":aA&&aA<600?"#f59e0b":"#ef4444", fontFamily: "var(--mono)", textAlign: "center", fontWeight: 600 }}>{aA ? fmtDur(aA) : "—"}</div>
                    <div style={{ color: aB&&aB>=4?"#22c55e":aB&&aB>=3?"#f59e0b":"#ef4444", fontFamily: "var(--mono)", textAlign: "center", fontWeight: 600 }}>{aB || "—"}</div>
                    <div style={{ color: s.ok>0?"#22c55e":"var(--dim)", fontFamily: "var(--mono)", textAlign: "center", fontWeight: 700 }}>{s.ok}</div>
                  </React.Fragment>
                );
              })}
            </div>
            <div style={{ marginTop: 8, padding: "5px 7px", background: "var(--bg)", borderRadius: 5, fontSize: 9, color: "var(--muted)", lineHeight: 1.5 }}>
              <b style={{ color: "var(--subtle)" }}>Lectura:</b> Barras altas + tiempo bajo = máxima efectividad.
            </div>
          </div>
        )}

        {/* Operator table */}
        {Object.keys(opStats).length > 0 && (
          <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 9, padding: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--subtle)", marginBottom: 8 }}>Rendimiento operadores CCTV</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 70px 70px 60px 70px", gap: "4px 8px", fontSize: 10 }}>
              <div style={TH}>Operador</div>
              <div style={{ ...TH, textAlign: "center" }}>Despachos</div>
              <div style={{ ...TH, textAlign: "center" }}>Cierres</div>
              <div style={{ ...TH, textAlign: "center" }}>Exitosos</div>
              <div style={{ ...TH, textAlign: "center" }}>T. Reac.</div>
              {Object.entries(opStats).map(([n, s]) => {
                const avgReact = s.reactionTimes && s.reactionTimes.length
                  ? Math.round(s.reactionTimes.reduce((a, b) => a + b, 0) / s.reactionTimes.length)
                  : null;
                return (
                  <React.Fragment key={n}>
                    <div style={{ color: "var(--text)", fontWeight: 600, overflow: "visible", textOverflow: "clip", whiteSpace: "normal", lineHeight: 1.3, wordBreak: "break-word" }}>{n}</div>
                    <div style={{ color: "var(--subtle)", fontFamily: "var(--mono)", textAlign: "center" }}>{s.disp}</div>
                    <div style={{ color: "var(--subtle)", fontFamily: "var(--mono)", textAlign: "center" }}>{s.close}</div>
                    <div style={{ color: s.ok>0?"#22c55e":"var(--dim)", fontFamily: "var(--mono)", textAlign: "center", fontWeight: 700 }}>{s.ok}</div>
                    <div style={{ color: avgReact&&avgReact<60?"#22c55e":avgReact&&avgReact<180?"#f59e0b":"#ef4444", fontFamily: "var(--mono)", textAlign: "center", fontWeight: 600 }}>
                      {avgReact ? fmtDur(avgReact) : "—"}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}
      </div>
      </div> {/* pdf-slide-3 */}

      <div id="pdf-slide-4">
        {/* Slide 4 Header (Looker Studio style, visible only in PDF) */}
        <div className="only-print-presentation" style={{
          display: "none",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
          borderBottom: "1px solid var(--border)",
          paddingBottom: 8,
          width: "100%"
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "var(--bright)" }}>Registro de Eventos con Hallazgos</div>
            <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>
              {facility?.name || "Recinto"} · Eventos de {periodLabel}
            </div>
          </div>
          <div style={{ fontSize: 9, color: "var(--muted)", fontFamily: "var(--mono)" }}>
            Generado: {new Date().toLocaleDateString("es-CL")}
          </div>
        </div>

        {/* Event history */}
        <div className="pdf-history-container" style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 9, padding: 12 }}>
          <div className="no-print" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--subtle)" }}>Historial ({filtered.length})</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <select
                value={filterResult}
                onChange={e => setFilterResult(e.target.value)}
                className="v3-input"
                style={{ maxWidth: 150, padding: "5px 8px", fontSize: 10, background: "var(--surface)", color: "var(--bright)", border: "1px solid var(--border)", borderRadius: 6 }}
              >
                <option value="all">Todos los resultados</option>
                <option value="with_findings">Gestión con hallazgo</option>
                <option value="without_findings">Gestión sin hallazgo</option>
                <option value="drill">Simulacro / Prueba</option>
                <option value="unmanaged">Sin gestión / Cierre auto</option>
              </select>
              <input
                className="v3-input"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Buscar..."
                style={{ maxWidth: 150, padding: "5px 8px", fontSize: 10 }}
              />
              {isSupervisor && (
                <>
                  <button
                    type="button"
                    onClick={handleExportCSV}
                    style={{
                      padding: "6px 10px", borderRadius: 6, fontSize: 10, fontWeight: 700,
                      background: "#22c55e20", color: "#22c55e", border: "1px solid #22c55e40",
                      cursor: "pointer", display: "flex", alignItems: "center", gap: 3
                    }}
                  >
                    📥 Excel
                  </button>
                  <button
                    type="button"
                    onClick={handleExportPDF}
                    style={{
                      padding: "6px 10px", borderRadius: 6, fontSize: 10, fontWeight: 700,
                      background: "rgba(168, 85, 247, 0.15)", color: "#a855f7", border: "1px solid rgba(168, 85, 247, 0.3)",
                      cursor: "pointer", display: "flex", alignItems: "center", gap: 3
                    }}
                  >
                    📄 PDF Presentación
                  </button>
                  <button
                    type="button"
                    onClick={handlePrintPaper}
                    style={{
                      padding: "6px 10px", borderRadius: 6, fontSize: 10, fontWeight: 700,
                      background: "rgba(59, 130, 246, 0.15)", color: "#3b82f6", border: "1px solid rgba(59, 130, 246, 0.3)",
                      cursor: "pointer", display: "flex", alignItems: "center", gap: 3
                    }}
                  >
                    🖨️ PDF Impresión
                  </button>
                </>
              )}
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: saving 
                ? "0.9fr 1.1fr 0.5fr 0.8fr 0.8fr 0.9fr 1.2fr 0.8fr 1.3fr 1.8fr" 
                : "1.5fr 1.5fr 0.7fr 1.2fr 1.2fr 1.2fr 1.2fr 2fr",
              gap: 4, padding: "0 0 6px 0",
              borderBottom: "2px solid var(--border)",
              alignItems: "center",
              overflow: "visible",
              minWidth: saving ? "auto" : 1000
            }}>
              <div style={{ ...TH, overflow: "visible", paddingTop: "2px", paddingBottom: "2px" }}>Zona</div>
              <div style={{ ...TH, overflow: "visible", paddingTop: "2px", paddingBottom: "2px" }}>Fecha/Hora</div>
              <div style={{ ...TH, overflow: "visible", paddingTop: "2px", paddingBottom: "2px" }}>Dur.</div>
              <div style={{ ...TH, overflow: "visible", paddingTop: "2px", paddingBottom: "2px" }}>CCTV</div>
              <div style={{ ...TH, overflow: "visible", paddingTop: "2px", paddingBottom: "2px" }}>Guardia</div>
              <div style={{ ...TH, overflow: "visible", paddingTop: "2px", paddingBottom: "2px", textAlign: "center" }}>Resultado</div>
              {saving && <div style={{ ...TH, overflow: "visible", paddingTop: "2px", paddingBottom: "2px" }}>Situación</div>}
              <div style={{ ...TH, overflow: "visible", paddingTop: "2px", paddingBottom: "2px" }}>Patente</div>
              <div style={{ ...TH, overflow: "visible", paddingTop: "2px", paddingBottom: "2px" }}>Sospechoso</div>
              {saving && <div style={{ ...TH, overflow: "visible", paddingTop: "2px", paddingBottom: "2px" }}>Comentarios</div>}
            </div>

            {(() => {
              const listToRender = saving ? (() => {
                const relevantEventsOnly = filtered.filter(a => {
                  const res = normalizeResult(a.closeResult || a.reasonForResolution);
                  const situation = String(a.situation || "");
                  return res === "with_findings" || situation === "6" || a.plate || a.vehicle?.plate;
                });
                const getPriorityScore = (a) => {
                  let score = 0;
                  if (a.plate || a.vehicle?.plate) score += 1000;
                  const sit = String(a.situation || "");
                  if (sit === "6") score += 500;
                  else if (sit === "5") score += 400;
                  else if (sit === "4") score += 300;
                  else if (sit === "7") score += 200;
                  if (a.notes || a.reasonForResolutionDetails) score += 50;
                  return score;
                };
                return [...relevantEventsOnly].sort((a, b) => {
                  const scoreA = getPriorityScore(a);
                  const scoreB = getPriorityScore(b);
                  if (scoreB !== scoreA) return scoreB - scoreA;
                  const timeA = a.createdAt?.seconds || 0;
                  const timeB = b.createdAt?.seconds || 0;
                  return timeB - timeA;
                }).slice(0, 13);
              })() : filtered;

              return listToRender.map(a => {
                const startMs = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : null;
                const isClosed = !!a.resolvedAt;
                const situation = String(a.situation || "");
                const res = normalizeResult(a.closeResult || a.reasonForResolution);
                const isRelevant = res === "with_findings" || situation === "6" || a.plate || a.vehicle?.plate;
                return (
                  <div
                    key={a.docID || startMs}
                    role="button"
                    className={isRelevant ? "relevant-event" : "common-event"}
                    onClick={() => isClosed && setDetail(a)}
                    style={{
                      display: "grid",
                      gridTemplateColumns: saving 
                        ? "0.9fr 1.1fr 0.5fr 0.8fr 0.8fr 0.9fr 1.2fr 0.8fr 1.3fr 1.8fr" 
                        : "1.5fr 1.5fr 0.7fr 1.2fr 1.2fr 1.2fr 1.2fr 2fr",
                      gap: 4, padding: saving ? "4px 0" : "8px 0",
                      fontSize: 9, alignItems: "center",
                      width: "100%",
                      borderBottom: "1px solid var(--border)",
                      color: "var(--text)", cursor: isClosed ? "pointer" : "default",
                      textAlign: "left",
                      overflow: "visible",
                      minWidth: saving ? "auto" : 1000
                    }}
                  >
                    <span style={{ fontWeight: 600, overflow: "visible", whiteSpace: "normal", lineHeight: 1.3, wordBreak: "break-word", paddingTop: "2px", paddingBottom: "2px" }}>{a.zoneName}</span>
                    <span style={{ color: "var(--muted)", fontFamily: "var(--mono)", overflow: "visible", whiteSpace: "normal", lineHeight: 1.3, wordBreak: "break-word", paddingTop: "2px", paddingBottom: "2px" }}>
                      {startMs ? new Date(startMs).toLocaleString("es-CL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}
                    </span>
                    <span style={{ color: "var(--subtle)", overflow: "visible", whiteSpace: "normal", lineHeight: 1.3, paddingTop: "2px", paddingBottom: "2px" }}>{a.duration != null ? fmtDur(a.duration) : "—"}</span>
                    <span style={{ color: "var(--muted)", overflow: "visible", whiteSpace: "normal", lineHeight: 1.3, wordBreak: "break-word", paddingTop: "2px", paddingBottom: "2px" }}>{a.dispatchedBy || a.guardName || "—"}</span>
                    <span style={{ color: "var(--muted)", overflow: "visible", whiteSpace: "normal", lineHeight: 1.3, wordBreak: "break-word", paddingTop: "2px", paddingBottom: "2px" }}>
                      {(Array.isArray(a.guards) ? a.guards[0] : null) || "—"}
                    </span>
                    {isClosed ? (
                      <span style={{
                        padding: "3px 6px", borderRadius: 3, fontSize: 9, fontWeight: 700, textAlign: "center",
                        background: `${getResultColor(a)}15`, color: getResultColor(a),
                        lineHeight: 1.1, display: "inline-flex", alignItems: "center", justifyContent: "center",
                        marginTop: "2px", marginBottom: "2px"
                      }}>{getResultLabel(a)}</span>
                    ) : (
                      <span style={{
                        padding: "3px 6px", borderRadius: 3, fontSize: 9, fontWeight: 700, background: "#dc262615", color: "#dc2626",
                        textAlign: "center", lineHeight: 1.1, display: "inline-flex", alignItems: "center", justifyContent: "center",
                        marginTop: "2px", marginBottom: "2px"
                      }}>{a.resolvedAt ? getResultLabel(a) : "ABIERTO"}</span>
                    )}
                    {saving && (
                      <span style={{ color: "var(--text)", overflow: "visible", whiteSpace: "normal", lineHeight: 1.3, wordBreak: "break-word", paddingTop: "2px", paddingBottom: "2px" }}>
                        {SITUATIONS_MAP[a.situation] || a.situation || "—"}
                      </span>
                    )}
                    <span style={{ color: "var(--muted)", fontFamily: "var(--mono)", overflow: "visible", whiteSpace: "normal", lineHeight: 1.3, wordBreak: "break-word", paddingTop: "2px", paddingBottom: "2px" }}>
                      {a.vehicle?.plate || a.plate || "—"}
                    </span>
                    <span style={{ color: "var(--text)", overflow: "visible", whiteSpace: "normal", lineHeight: 1.3, wordBreak: "break-word", paddingTop: "2px", paddingBottom: "2px" }}>
                      {a.suspectDesc || (a.suspect ? "Sí" : "—")}
                    </span>
                    {saving && (
                      <span style={{ color: "var(--subtle)", overflow: "visible", whiteSpace: "normal", lineHeight: 1.3, wordBreak: "break-word", fontSize: 8.5, paddingTop: "2px", paddingBottom: "2px" }}>
                        {a.notes || a.reasonForResolutionDetails || "—"}
                      </span>
                    )}
                  </div>
                );
              });
            })()}
          </div>
          <div style={{ marginTop: 6, fontSize: 9, color: "var(--muted)", fontFamily: "var(--mono)", textAlign: "center" }}>
            Clic en evento cerrado para ver detalle completo
          </div>
        </div>
      </div> {/* pdf-slide-4 */}
      </div> {/* pdf-slides-export-container */}

      {detail && (
        <EventDetail
          alarm={detail}
          onClose={() => setDetail(null)}
          reviewRoles={reviewRoles.canReview ? reviewRoles : undefined}
          onConfirm={async (a) => { try { await confirmEvent(a, user); } catch (e) { console.error(e); } setDetail(null); }}
          onValidate={async (a) => { try { await validateEvent(a, user, facility?.id); } catch (e) { console.error(e); } setDetail(null); }}
          onEdit={(a) => { setDetail(null); setEditAlarm(a); }}
          onGuide={() => setShowGuide(true)}
          onCaseReport={(a) => { setDetail(null); setReportCases([a]); }}
        />
      )}

      {/* ── Fase 2: bandeja de revisión, guía y edición desde el supervisor ── */}
      {showInbox && reviewRoles.canReview && (
        <ReviewInbox
          alarms={filtered}
          roles={reviewRoles}
          user={user}
          facilityId={facility?.id}
          onEdit={(a) => setEditAlarm(a)}
          onOpenDetail={(a) => setDetail(a)}
          onGuide={() => setShowGuide(true)}
          onReport={(list) => setReportCases(list)}
          onClose={() => setShowInbox(false)}
        />
      )}
      {editAlarm && (
        <CloseAlarmModal
          alarmObj={editAlarm}
          facilityId={facility.id}
          operators={facility?.operators || []}
          knownGuards={facility?.guards || []}
          nameAliases={facility?.nameAliases}
          showSiren={facility?.showSiren ?? false}
          isReopen={true}
          reviewer={{ canReview: reviewRoles.canReview, name: user?.email || null, uid: user?.uid || null }}
          onClose={() => setEditAlarm(null)}
        />
      )}
      {showGuide && <SituationGuide onClose={() => setShowGuide(false)} />}
      {reportCases && (
        <CaseReport
          cases={reportCases}
          facility={facility}
          user={user}
          periodLabel={period === "day" ? "Hoy" : period === "week" ? "Esta semana" : "Este mes"}
          totalRelevant={filtered.filter(a => a.resolvedAt && isRelevantEvent(a)).length}
          onClose={() => setReportCases(null)}
        />
      )}

      {/* ── Settings Modal ── */}
      {showSettings && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(15, 23, 42, 0.8)", backdropFilter: "blur(8px)",
          zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
          padding: 16
        }}>
          <div style={{
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            width: "100%",
            maxWidth: 480,
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.7), 0 10px 10px -5px rgba(0, 0, 0, 0.7)",
            color: "var(--bright)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
          }}>
            {/* Header */}
            <div style={{
              padding: "16px 20px",
              borderBottom: "1px solid var(--border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}>
              <div style={{ fontSize: 13, fontWeight: 800, display: "flex", alignItems: "center", gap: 6 }}>
                <span>⚙️</span> Opciones de Monitoreo
              </div>
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: "var(--muted)", fontSize: 16, padding: 4
                }}
              >
                ✕
              </button>
            </div>

            {/* Content Form */}
            <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16, overflowY: "auto", maxHeight: "calc(100vh - 120px)" }}>
              {/* SECTION 1: Turn Hours */}
              <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: hoursActive ? 6 : 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                    Filtro de Horario de Operación
                  </span>
                  <div
                    onClick={() => isSupervisor && setHoursActive(!hoursActive)}
                    style={{
                      position: "relative",
                      width: 38,
                      height: 20,
                      background: hoursActive ? "#22c55e" : "var(--border)",
                      borderRadius: 10,
                      cursor: isSupervisor ? "pointer" : "default",
                      transition: "background 0.2s ease",
                      opacity: isSupervisor ? 1 : 0.6
                    }}
                  >
                    <div style={{
                      position: "absolute",
                      top: 2,
                      left: hoursActive ? 20 : 2,
                      width: 16,
                      height: 16,
                      background: "#fff",
                      borderRadius: "50%",
                      transition: "left 0.2s ease",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.4)"
                    }} />
                  </div>
                </div>
                <div style={{ fontSize: 9, color: "var(--muted)", marginBottom: hoursActive ? 12 : 0 }}>
                  Define una ventana horaria de monitoreo. Fuera de este rango, las alertas se ocultan del panel principal para evitar ruidos fuera de turno.
                </div>
                
                {hoursActive && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
                    <div>
                      <div style={{ fontSize: 9, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Hora Inicio</div>
                      <select
                        value={startHour}
                        disabled={!isSupervisor}
                        onChange={(e) => setStartHour(Number(e.target.value))}
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          background: "var(--surface)",
                          border: "1px solid var(--border)",
                          borderRadius: 6,
                          color: "var(--bright)",
                          fontSize: 12
                        }}
                      >
                        {Array.from({ length: 24 }).map((_, h) => (
                          <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Hora Término</div>
                      <select
                        value={endHour}
                        disabled={!isSupervisor}
                        onChange={(e) => setEndHour(Number(e.target.value))}
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          background: "var(--surface)",
                          border: "1px solid var(--border)",
                          borderRadius: 6,
                          color: "var(--bright)",
                          fontSize: 12
                        }}
                      >
                        {Array.from({ length: 24 }).map((_, h) => (
                          <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* SECTION 2: Auto Close */}
              <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: autoCloseActive ? 6 : 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                    Cierre Automático por Inactividad
                  </span>
                  <div
                    onClick={() => isSupervisor && setAutoCloseActive(!autoCloseActive)}
                    style={{
                      position: "relative",
                      width: 38,
                      height: 20,
                      background: autoCloseActive ? "#22c55e" : "var(--border)",
                      borderRadius: 10,
                      cursor: isSupervisor ? "pointer" : "default",
                      transition: "background 0.2s ease",
                      opacity: isSupervisor ? 1 : 0.6
                    }}
                  >
                    <div style={{
                      position: "absolute",
                      top: 2,
                      left: autoCloseActive ? 20 : 2,
                      width: 16,
                      height: 16,
                      background: "#fff",
                      borderRadius: "50%",
                      transition: "left 0.2s ease",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.4)"
                    }} />
                  </div>
                </div>
                <div style={{ fontSize: 9, color: "var(--muted)", marginBottom: autoCloseActive ? 12 : 0 }}>
                  Resuelve automáticamente alertas que no registren interacción tras el periodo indicado, evitando reportes de gestión inconclusos.
                </div>
                
                {autoCloseActive && (
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10, marginTop: 8 }}>
                    <div>
                      <div style={{ fontSize: 9, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Duración Máxima</div>
                      <input
                        type="number"
                        min="1"
                        value={closeTime}
                        disabled={!isSupervisor}
                        onChange={(e) => setCloseTime(Number(e.target.value))}
                        style={{
                          width: "100%",
                          padding: "5px 8px",
                          background: "var(--surface)",
                          border: "1px solid var(--border)",
                          borderRadius: 6,
                          color: "var(--bright)",
                          fontSize: 12,
                          boxSizing: "border-box"
                        }}
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Unidad</div>
                      <select
                        value={closeUnit}
                        disabled={!isSupervisor}
                        onChange={(e) => setCloseUnit(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          background: "var(--surface)",
                          border: "1px solid var(--border)",
                          borderRadius: 6,
                          color: "var(--bright)",
                          fontSize: 12
                        }}
                      >
                        <option value="hours">Horas</option>
                        <option value="minutes">Minutos</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* SECTION 3: Recordatorios de Gestión */}
              <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: mgmtAlertsActive ? 6 : 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                    Recordatorios de Gestión CCTV
                  </span>
                  <div
                    onClick={() => isSupervisor && setMgmtAlertsActive(!mgmtAlertsActive)}
                    style={{
                      position: "relative",
                      width: 38,
                      height: 20,
                      background: mgmtAlertsActive ? "#22c55e" : "var(--border)",
                      borderRadius: 10,
                      cursor: isSupervisor ? "pointer" : "default",
                      transition: "background 0.2s ease",
                      opacity: isSupervisor ? 1 : 0.6
                    }}
                  >
                    <div style={{
                      position: "absolute",
                      top: 2,
                      left: mgmtAlertsActive ? 20 : 2,
                      width: 16,
                      height: 16,
                      background: "#fff",
                      borderRadius: "50%",
                      transition: "left 0.2s ease",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.4)"
                    }} />
                  </div>
                </div>
                <div style={{ fontSize: 9, color: "var(--muted)", marginBottom: mgmtAlertsActive ? 12 : 0 }}>
                  Emite alertas visuales y auditivas en la pantalla del operador si los eventos exceden los tiempos máximos permitidos sin despacho o cierre.
                </div>
                
                {mgmtAlertsActive && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
                    <div>
                      <div style={{ fontSize: 9, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>T. Máx Despacho</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <input
                          type="number"
                          min="10"
                          value={alertSecsDisp}
                          disabled={!isSupervisor}
                          onChange={(e) => setAlertSecsDisp(Number(e.target.value))}
                          style={{
                            width: "100%",
                            padding: "5px 8px",
                            background: "var(--surface)",
                            border: "1px solid var(--border)",
                            borderRadius: 6,
                            color: "var(--bright)",
                            fontSize: 12
                          }}
                        />
                        <span style={{ fontSize: 10, color: "var(--muted)" }}>seg</span>
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>T. Máx Cierre</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <input
                          type="number"
                          min="1"
                          value={alertMinsClose}
                          disabled={!isSupervisor}
                          onChange={(e) => setAlertMinsClose(Number(e.target.value))}
                          style={{
                            width: "100%",
                            padding: "5px 8px",
                            background: "var(--surface)",
                            border: "1px solid var(--border)",
                            borderRadius: 6,
                            color: "var(--bright)",
                            fontSize: 12
                          }}
                        />
                        <span style={{ fontSize: 10, color: "var(--muted)" }}>min</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* SECTION 4: Silenciamiento Programado */}
              <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em", display: "block", marginBottom: 4 }}>
                  Silenciamientos Programados
                </span>
                <div style={{ fontSize: 9, color: "var(--muted)", marginBottom: 10 }}>
                  Ignora automáticamente alertas generadas por pruebas de antenas o señales recurrentes conocidas en los intervalos definidos.
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                  <div style={{ display: "flex", gap: 4 }}>
                    <input
                      type="text"
                      placeholder="Nombre (ej. Test Antena)..."
                      disabled={!isSupervisor}
                      value={newRuleName}
                      onChange={(e) => setNewRuleName(e.target.value)}
                      style={{
                        flex: 1,
                        padding: "5px 8px",
                        background: "var(--surface)",
                        border: "1px solid var(--border)",
                        borderRadius: 6,
                        color: "var(--bright)",
                        fontSize: 11
                      }}
                    />
                    <input
                      type="text"
                      placeholder="09:58"
                      disabled={!isSupervisor}
                      value={newRuleStart}
                      onChange={(e) => setNewRuleStart(e.target.value)}
                      style={{
                        width: 50,
                        padding: "5px 8px",
                        background: "var(--surface)",
                        border: "1px solid var(--border)",
                        borderRadius: 6,
                        color: "var(--bright)",
                        fontSize: 11,
                        textAlign: "center"
                      }}
                    />
                    <span style={{ color: "var(--muted)", alignSelf: "center", fontSize: 10 }}>a</span>
                    <input
                      type="text"
                      placeholder="10:02"
                      disabled={!isSupervisor}
                      value={newRuleEnd}
                      onChange={(e) => setNewRuleEnd(e.target.value)}
                      style={{
                        width: 50,
                        padding: "5px 8px",
                        background: "var(--surface)",
                        border: "1px solid var(--border)",
                        borderRadius: 6,
                        color: "var(--bright)",
                        fontSize: 11,
                        textAlign: "center"
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    disabled={!isSupervisor || !newRuleName.trim() || !newRuleStart.trim() || !newRuleEnd.trim()}
                    onClick={() => {
                      const name = newRuleName.trim();
                      const start = newRuleStart.trim();
                      const end = newRuleEnd.trim();
                      if (name && start && end) {
                        setQuietRules([...quietRules, { name, start, end }]);
                        setNewRuleName("");
                        setNewRuleStart("09:58");
                        setNewRuleEnd("10:02");
                      }
                    }}
                    style={{
                      width: "100%",
                      padding: "5px",
                      background: "rgba(59, 130, 246, 0.1)",
                      color: "#3b82f6",
                      border: "1px solid rgba(59, 130, 246, 0.2)",
                      borderRadius: 6,
                      fontSize: 10,
                      fontWeight: 700,
                      cursor: "pointer"
                    }}
                  >
                    + Agregar Regla de Silenciamiento
                  </button>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 5, maxHeight: 110, overflowY: "auto", paddingRight: 2 }}>
                  {quietRules.length === 0 ? (
                    <div style={{ fontSize: 10, color: "var(--muted)", fontStyle: "italic" }}>No hay exclusiones horarias configuradas.</div>
                  ) : (
                    quietRules.map((r, idx) => (
                      <div key={idx} style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background: "var(--surface)",
                        border: "1px solid var(--border)",
                        padding: "6px 10px",
                        borderRadius: 6,
                        gap: 8
                      }}>
                        <div style={{
                          minWidth: 0,
                          flex: 1,
                          fontSize: 10,
                          fontWeight: 700,
                          color: "var(--bright)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}>
                          {r.name}
                        </div>
                        <div style={{
                          flexShrink: 0,
                          fontSize: 10,
                          fontFamily: "var(--mono)",
                          background: "rgba(59, 130, 246, 0.08)",
                          color: "#3b82f6",
                          padding: "2px 6px",
                          borderRadius: 4,
                          fontWeight: 700
                        }}>
                          {r.start} - {r.end}
                        </div>
                        {isSupervisor && (
                          <button
                            type="button"
                            onClick={() => setQuietRules(quietRules.filter((_, i) => i !== idx))}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#ef4444",
                              fontSize: 10,
                              cursor: "pointer",
                              padding: "2px 4px",
                              flexShrink: 0
                            }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* SECTION 5: Notificaciones de Telegram */}
              <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em", display: "block", marginBottom: 4 }}>
                  Alertas Telegram a Jefaturas
                </span>
                <div style={{ fontSize: 9, color: "var(--muted)", marginBottom: 10 }}>
                  Reporta de forma automática y en tiempo real eventos operacionales clave del recinto al supervisor registrado.
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 9, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>IDs de Chat Telegram</div>
                  <input
                    type="text"
                    placeholder="Ej. -100123456, 987654321..."
                    disabled={!isSupervisor}
                    value={teleChatIds}
                    onChange={(e) => setTeleChatIds(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "5px 8px",
                      background: "var(--surface)",
                      border: "1px solid var(--border)",
                      borderRadius: 6,
                      color: "var(--bright)",
                      fontSize: 11,
                      boxSizing: "border-box"
                    }}
                  />
                  <div style={{ fontSize: 8, color: "var(--muted)", marginTop: 2 }}>Registra múltiples Chat IDs separados por comas (chats directos o de grupos).</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {[
                    [teleNotifyRelevant, setTeleNotifyRelevant, "Eventos Relevantes", "Aviso inmediato (0 min) al registrarse hallazgos de guardias, patentes o delincuentes."],
                    [teleNotifyPersistent, setTeleNotifyPersistent, "Señales Persistentes", "Aviso inmediato (0 min) al confirmarse señal de jammer continua en el recinto."],
                    [teleNotifyUnmanaged, setTeleNotifyUnmanaged, "Alarmas Sin Gestión", "Aviso retrasado (15 min) al detectarse alarmas que no han sido atendidas."]
                  ].map(([val, setVal, label, desc]) => (
                    <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--bright)" }}>{label}</div>
                        <div style={{ fontSize: 8.5, color: "var(--muted)" }}>{desc}</div>
                      </div>
                      <div
                        onClick={() => isSupervisor && setVal(!val)}
                        style={{
                          position: "relative",
                          width: 32,
                          height: 16,
                          background: val ? "#3b82f6" : "var(--border)",
                          borderRadius: 8,
                          cursor: isSupervisor ? "pointer" : "default",
                          transition: "background 0.2s ease",
                          opacity: isSupervisor ? 1 : 0.6,
                          flexShrink: 0
                        }}
                      >
                        <div style={{
                          position: "absolute",
                          top: 1.5,
                          left: val ? 17.5 : 1.5,
                          width: 13,
                          height: 13,
                          background: "#fff",
                          borderRadius: "50%",
                          transition: "left 0.2s ease"
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* SECTION 6: Gestión de nombres de personal (dedup) */}
              <NameManagerSection
                facilities={facilitiesList.length > 0 ? facilitiesList : [facility]}
                zonesIds={zonesIds}
                alarms={alarms}
                isSupervisor={isSupervisor}
                isDev={isDevUser}
                user={user}
              />

              {/* SECTION 3: Permanent Warning Alert */}
              <div style={{
                background: "rgba(220, 38, 38, 0.05)",
                border: "1px solid rgba(220, 38, 38, 0.15)",
                borderRadius: 8,
                padding: "10px 14px",
                fontSize: 10,
                lineHeight: 1.4,
                color: "var(--subtle)"
              }}>
                <span style={{ fontWeight: 700, color: "#f87171", display: "block", marginBottom: 3 }}>
                  ⚠️ AVISO DEL SISTEMA:
                </span>
                Todo evento no despachado con más de <b>48 horas</b> de antigüedad se cerrará de forma automática como "Sin gestión / cierre automático" por resguardo y políticas de almacenamiento de datos.
              </div>

              {errorMsg && (
                <div style={{ color: "#ef4444", fontSize: 11, fontWeight: 600 }}>{errorMsg}</div>
              )}
            </div>

            {/* Footer buttons / permissions alert */}
            <div style={{
              padding: "12px 20px",
              borderTop: "1px solid var(--border)",
              background: "rgba(15, 23, 42, 0.2)",
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              alignItems: "center"
            }}>
              {!isSupervisor ? (
                <div style={{
                  color: "#f59e0b",
                  fontSize: 10,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  width: "100%"
                }}>
                  <span>⚠️</span> Solo lectura: Requiere rol de supervisor para editar.
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setShowSettings(false)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 6,
                      background: "none",
                      border: "1px solid var(--border)",
                      color: "var(--muted)",
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer"
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={handleSaveSettings}
                    style={{
                      padding: "6px 16px",
                      borderRadius: 6,
                      background: "#22c55e",
                      border: "none",
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                      opacity: saving ? 0.6 : 1
                    }}
                  >
                    {saving ? "Guardando..." : "Guardar Cambios"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
